import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import useAuthStore from "@/store/authStore";
import PermButton from "@/components/PermButton";
// R13 (Jul 2026) — CheckSquare + Lock icons removed alongside the
// CONFIRMED/lock UI they used.
import { Plus, TrendingUp, Upload, Download, ChevronDown, ChevronUp, DollarSign, FileSpreadsheet, X, Package, Pencil, Trash2, AlertTriangle, Filter, History, FileText, Factory, HelpCircle } from "lucide-react";
import ForecastSummary from "../components/ForecastSummary";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import Pagination from "../components/Pagination";
import { filterLatestPlanRows } from "../lib/latestPlanRows";
import { filterProductionSummaryRows } from "../lib/productionSummaryFilter";
import { BuyerSkuSearchBox } from "../components/BuyerSkuSearchBox";
import { shouldShowAllocateSection } from "../lib/dispatchAllocateVisibility";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// R17+R20 (Jul 2026) — dual-badge status renderer. Consumes the triple
// (override_status, time_status, qty_status) and never falls back to
// any single-string status field. Rendering rules:
//   • override present + NO_SCHEDULE  → one badge (Override only).
//   • override present + COMPLETED    → two badges (Override + Qty),
//                                       because COMPLETED carries
//                                       qty_status ∈ {MATCHES_FORECAST,
//                                       EXCESS} — surplus vs on-target
//                                       is real signal to the planner.
//   • no override                     → two badges (Time + Qty), one
//                                       per axis, computed independently.
// Any component reading forecast statuses in this file goes through
// <StatusBadges/>; the pre-Jul-2026 single-string vocabulary (flat
// `r.status` / `original_plan_status` / `latest_plan_status` fields
// plus the AT_RISK bucket) is retired end-to-end — backend response
// shape and frontend rendering both moved to the triple.
const STATUS_BADGE_STYLE = {
  OVERDUE:          "bg-red-100 border-red-300 text-red-700",
  ON_TRACK:         "bg-blue-100 border-blue-300 text-blue-700",
  SHORTFALL:        "bg-amber-100 border-amber-300 text-amber-700",
  EXCESS:           "bg-violet-100 border-violet-300 text-violet-700",
  MATCHES_FORECAST: "bg-emerald-100 border-emerald-300 text-emerald-700",
  NO_SCHEDULE:      "bg-zinc-100 border-zinc-300 text-zinc-600",
  COMPLETED:        "bg-emerald-100 border-emerald-300 text-emerald-700",
};
const STATUS_BADGE_LABEL = {
  OVERDUE: "Overdue",     ON_TRACK: "On Track",
  SHORTFALL: "Shortfall", EXCESS: "Excess",   MATCHES_FORECAST: "Matches",
  NO_SCHEDULE: "No Schedule", COMPLETED: "Completed",
};

const StatusBadges = ({ overrideStatus, timeStatus, qtyStatus, testIdPrefix, className = "" }) => {
  const chip =
    "inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border";
  const emit = (key, kind, value) => (
    <span
      key={key}
      className={`${chip} ${STATUS_BADGE_STYLE[value] || "bg-zinc-100 border-zinc-300 text-zinc-600"}`}
      data-testid={testIdPrefix ? `${testIdPrefix}-${kind}` : undefined}
    >
      {STATUS_BADGE_LABEL[value] || value}
    </span>
  );
  const chips = [];
  if (overrideStatus) {
    chips.push(emit("ov", "override", overrideStatus));
    // COMPLETED carries qty_status; NO_SCHEDULE has qty_status=null.
    if (qtyStatus) chips.push(emit("q", "qty", qtyStatus));
  } else {
    if (timeStatus) chips.push(emit("t", "time", timeStatus));
    if (qtyStatus) chips.push(emit("q", "qty", qtyStatus));
  }
  return <div className={`inline-flex gap-1 flex-wrap ${className}`}>{chips}</div>;
};

const Demand = () => {
  const { token, hasRole } = useAuthStore();
  const [activeTab, setActiveTab] = useState("forecasts");

  // ── Production Summary tab state ─────────────────────────────────
  // R17+R20 (Jul 2026) — Status vocabulary is the triple:
  //   override_status  ∈ {NO_SCHEDULE, COMPLETED, null}
  //   time_status      ∈ {OVERDUE, ON_TRACK, null}
  //   qty_status       ∈ {SHORTFALL, EXCESS, MATCHES_FORECAST, null}
  // Filters are three independent selects — Time / Qty / Override —
  // combined AND-across-dimensions but each dimension only ever
  // constrains its own row type (spec: "neither hides the other's
  // rows"). Filtering happens client-side so the row set covers all
  // combos without the backend needing three new query params.
  const [psMonth, setPsMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [psBuyerId, setPsBuyerId] = useState("");
  const [psTimeFilter, setPsTimeFilter] = useState("");       // "" | "OVERDUE" | "ON_TRACK"
  const [psQtyFilter, setPsQtyFilter] = useState("");         // "" | "SHORTFALL" | "EXCESS" | "MATCHES_FORECAST"
  const [psOverrideFilter, setPsOverrideFilter] = useState(""); // "" | "NO_SCHEDULE" | "COMPLETED"
  // Feb 2026 — identity filters that apply universally (override band
  // included). Combined AND with the status filters above.
  const [psModelFilter, setPsModelFilter] = useState("");
  const [psBrandFilter, setPsBrandFilter] = useState("");
  const [psSkuSearch, setPsSkuSearch] = useState("");
  // EXPIRED filtering was retired in the Jul 2026 shim cleanup along
  // with the backend `?status=` query param — the surface was a silent
  // no-op after the shim went away (the flat `r.status` filter it
  // relied on never matched "EXPIRED"), so the checkbox is gone.
  // Historical expired forecasts are still queryable by passing the
  // relevant `?month=YYYY-MM` explicitly.
  const [psRows, setPsRows] = useState([]);
  const [psSummary, setPsSummary] = useState({
    total: 0,
    overdue: 0, on_track: 0,
    shortfall: 0, excess: 0, matches_forecast: 0,
    no_schedule: 0, completed: 0,
  });
  const [psLoading, setPsLoading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Data
  const [forecasts, setForecasts] = useState([]);
  const [forecastsSummary, setForecastsSummary] = useState(null);
  const [buyers, setBuyers] = useState([]);
  const [skus, setSkus] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [models, setModels] = useState([]);
  const [brands, setBrands] = useState([]);
  
  // Selection state for bulk confirmation
  // R13 (Jul 2026) — `selectedForecasts` set + bulk-confirm handlers
  // removed alongside the CONFIRMED status. `selectedForecasts` is
  // retained as an inert empty Set so the row `bg-blue-50` highlight
  // condition (`selectedForecasts.has(f.id)`) evaluates false without
  // extra branching. Safe to delete once no consumer references it.
  const [selectedForecasts] = useState(new Set());
  const [expandedForecast, setExpandedForecast] = useState(null);
  
  // Filters for forecast list. `month` defaults to the current YYYY-MM so
  // planners land on the current month by default (per user request Feb 2026).
  // Setting to "" acts as "all months".
  const [forecastFilters, setForecastFilters] = useState(() => {
    const d = new Date();
    return {
      buyer_id: "",
      vertical_id: "",
      brand_id: "",
      model_id: "",
      month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      // Feb 2026 R14++ (AC3) — Zero / Non-zero / All. Defaults to
      // Non-zero on page load so the working set is the demand that
      // actually needs planning; safety-net zeroed rows are one click
      // away via this dropdown.
      quantity_filter: "nonzero",
    };
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Dispatch Lots popup state
  const [showLotsDialog, setShowLotsDialog] = useState(false);
  const [selectedForecastLots, setSelectedForecastLots] = useState({ forecast: null, lots: [] });
  const [buyerExistingLots, setBuyerExistingLots] = useState([]);
  const [addToLotQty, setAddToLotQty] = useState(0);
  const [selectedLotForAdd, setSelectedLotForAdd] = useState("");
  const [addingToLot, setAddingToLot] = useState(false);
  
  // Check if user can confirm forecasts - ONLY MASTER_ADMIN can confirm
  const canConfirmForecasts = false;
  
  // Check if user can create/edit forecasts
  const canEditForecasts = hasRole && (hasRole('MASTER_ADMIN') || hasRole('DEMAND_PLANNER'));
  
  // Dialogs
  const [showForecastDialog, setShowForecastDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadPreview, setUploadPreview] = useState([]);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [downloadingErrors, setDownloadingErrors] = useState(false);
  // Feb 2026 R14+ (AC8) — bulk-upload impact preview state. Populated
  // by /forecasts/bulk-upload/preview when the user selects a file;
  // drives the confirmation dialog that lists which buyer/months will
  // be versioned, which SKUs will be safety-net zeroed, and which
  // dispatch lots + production schedules will be re-linked. Blocks
  // commit until the user acknowledges.
  const [previewImpact, setPreviewImpact] = useState(null);
  const [pendingUploadFile, setPendingUploadFile] = useState(null);
  
  // Export Dialog state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    startDate: "",
    endDate: "",
    buyer_id: "",
    brand: "",
    model: "",
    status: ""
  });
  const [exporting, setExporting] = useState(false);
  
  // Feb 2026 — SKU search on the Forecast List. Kept independent of
  // `forecastFilters` (which triggers a server refetch) so typing
  // doesn't cause a fetch storm; substring-matched client-side
  // against the current page's `sku_id`.
  const [forecastSkuSearch, setForecastSkuSearch] = useState("");

  // Cascading filter state for forecast form
  const [forecastForm, setForecastForm] = useState({
    id: null, // null for new forecast, set for edit
    buyer_id: "",
    vertical_id: "",
    model_id: "",
    brand_id: "",
    sku_id: "",
    forecast_month: new Date().toISOString().slice(0, 7),
    quantity: 0,
    priority: "MEDIUM",
    notes: "",
    reason: ""
  });

  // R3+R4 Forecast Details dialog state (Feb 2026): shows version history
  // + production schedule history for a forecast. View-only.
  const [detailsForecast, setDetailsForecast] = useState(null); // the forecast row
  const [detailsSchedules, setDetailsSchedules] = useState([]); // production_schedules linked to it
  // R-status-unified (Feb 2026) — server-computed statuses for Block 1 / 2
  // fetched alongside detailsSchedules. See
  // GET /api/forecasts/{id}/plan-block-statuses.
  const [detailsPlanStatuses, setDetailsPlanStatuses] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // R13 (Jul 2026) — R5 lock state removed. `originalEditQuantity`
  // survives to power the "Reason required for downward revision" UX
  // (R4/R5 behaviour, unchanged by R13 per spec §WHAT IS NOT IN SCOPE).
  const [originalEditQuantity, setOriginalEditQuantity] = useState(null);

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    fetchForecasts();
    // Feb 2026 R14+ (AC10) — sku search now runs server-side, so it
    // must trigger a fetch (and reset the page cursor upstream via
    // the input's onChange handler).
  }, [currentPage, pageSize, forecastFilters, forecastSkuSearch]);

  // R8: Re-fetch the production-summary table whenever filters change
  // or the tab becomes active. Status filter is sent server-side so
  // sorting (OVERDUE → COMPLETED) is preserved.
  useEffect(() => {
    if (activeTab !== "production-summary") return;
    const fetchSummary = async () => {
      setPsLoading(true);
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const params = { month: psMonth };
        if (psBuyerId) params.buyer_id = psBuyerId;
        // Jul 2026 R17+R20 + shim cleanup — no status params passed;
        // the backend endpoint no longer accepts `?status=`. Time /
        // Qty / Override filtering all happens client-side (see
        // filterProductionSummaryRows below).
        const res = await axios.get(`${API}/forecasts/production-summary`, { params, headers });
        setPsRows(res.data?.rows || []);
        setPsSummary(res.data?.summary || {});
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load production summary");
        setPsRows([]);
      } finally {
        setPsLoading(false);
      }
    };
    fetchSummary();
  }, [activeTab, psMonth, psBuyerId, token]);

  // R8: Excel export — re-uses the same filter set as the table so
  // the user gets exactly what they see.
  const exportProductionSummary = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = { month: psMonth };
      if (psBuyerId) params.buyer_id = psBuyerId;
      // Jul 2026 — export follows the fetch: no status params. Full
      // month/buyer slice; client-side triple filters are display-only.
      const res = await axios.get(
        `${API}/forecasts/production-summary/export`,
        { params, headers, responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `forecast_production_summary_${psMonth}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to export production summary");
    }
  };

  // Client-side application of the Time / Qty / Override filters. See
  // /app/frontend/src/lib/productionSummaryFilter.js for the AND-across-
  // dimensions semantics and full Jest coverage. Backend still returns
  // the unfiltered month/buyer slice (plus optional EXPIRED); this is
  // where the triple slice is applied for display.
  // Feb 2026 — buyer_sku_id → identity map for client-side Model/Brand
  // filtering on Production Summary. Rows only carry `buyer_sku_id`;
  // the join back to `models`/`brands` happens here so no backend
  // change is needed.
  const psSkuLookup = useMemo(() => {
    const m = new Map();
    for (const s of skus || []) {
      if (s.buyer_sku_id) m.set(s.buyer_sku_id, { brand_id: s.brand_id, model_id: s.model_id });
    }
    return m;
  }, [skus]);

  const filteredPsRows = filterProductionSummaryRows(psRows, {
    timeFilter: psTimeFilter,
    qtyFilter: psQtyFilter,
    overrideFilter: psOverrideFilter,
    modelFilter: psModelFilter,
    brandFilter: psBrandFilter,
    skuSearch: psSkuSearch,
    skuLookup: psSkuLookup,
  });

  const fetchMasterData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [buyersRes, skusRes, verticalsRes, modelsRes, brandsRes] = await Promise.all([
        axios.get(`${API}/buyers`, { headers }),
        axios.get(`${API}/skus`, { headers }),
        axios.get(`${API}/verticals`, { headers }),
        axios.get(`${API}/models`, { headers }),
        axios.get(`${API}/brands`, { headers })
      ]);
      
      setBuyers(buyersRes.data);
      setSkus(skusRes.data);
      setVerticals(verticalsRes.data);
      setModels(modelsRes.data);
      setBrands(brandsRes.data);
      
    } catch (error) {
      console.error("Failed to fetch master data:", error);
      toast.error("Failed to fetch data");
    }
  };

  // Feb 2026 R14+ (AC9) — UTC-safe first-of-month builder. Fixes a
  // browser-timezone off-by-one where `new Date("YYYY-MM-01").toISOString()`
  // in a timezone ahead of UTC (e.g. IST) parsed the string as local
  // midnight, then rolled BACK into the previous day/month on
  // `.toISOString()`. Same helper used by `handleCreateForecast` and
  // the bulk-upload preview.
  const buildFirstOfMonthISO = (yyyyMm) => {
    if (!yyyyMm) return null;
    const [y, m] = String(yyyyMm).split("-").map((n) => parseInt(n, 10));
    if (!y || !m) return null;
    return new Date(Date.UTC(y, m - 1, 1)).toISOString();
  };

  const fetchForecasts = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API}/forecasts?page=${currentPage}&page_size=${pageSize}`;
      if (forecastFilters.buyer_id) url += `&buyer_id=${forecastFilters.buyer_id}`;
      if (forecastFilters.vertical_id) url += `&vertical_id=${forecastFilters.vertical_id}`;
      // Aug 2026 — Brand + Model filters. Backend resolves each to a
      // matching sku_id set on `buyer_skus` (brand) + `bidso_skus`
      // (model, two-hop via buyer_skus.bidso_sku_id) and layers into
      // the forecast query. Empty string → filter cleared → not sent,
      // matching the pattern of the other dropdowns above.
      if (forecastFilters.brand_id) url += `&brand_id=${forecastFilters.brand_id}`;
      if (forecastFilters.model_id) url += `&model_id=${forecastFilters.model_id}`;
      if (forecastFilters.month) url += `&month=${forecastFilters.month}`;
      // Feb 2026 R14++ (AC3) — Send quantity_filter unless user asked
      // for "All"; keeps URLs short and preserves backward-compatible
      // API behaviour for consumers that never send the param.
      if (forecastFilters.quantity_filter && forecastFilters.quantity_filter !== "all") {
        url += `&quantity_filter=${forecastFilters.quantity_filter}`;
      }
      // Feb 2026 R14+ (AC10) — SKU search runs server-side across the
      // full filtered dataset, not just the currently-loaded page. The
      // backend $or-matches both `forecast_code` and `sku_id`.
      if (forecastSkuSearch && forecastSkuSearch.trim()) {
        url += `&search=${encodeURIComponent(forecastSkuSearch.trim())}`;
      }
      
      const res = await axios.get(url, { headers });
      setForecasts(res.data.items || []);
      setForecastsSummary(res.data.summary || null);
      setTotalItems(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (error) {
      console.error("Failed to fetch forecasts:", error);
      toast.error("Failed to fetch forecasts");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    await fetchMasterData();
    await fetchForecasts();
  };

  // Cascading filter logic
  const getFilteredVerticals = () => {
    // Filter verticals based on buyer if buyer has associated verticals
    return verticals;
  };

  const getFilteredModels = () => {
    if (!forecastForm.vertical_id) return models;
    return models.filter(m => m.vertical_id === forecastForm.vertical_id);
  };

  const getFilteredBrands = () => {
    // Get brands from multiple sources
    let filteredBrands = [...brands];
    
    // If a vertical is selected, get brands from SKUs in that vertical
    if (forecastForm.vertical_id) {
      const verticalName = verticals.find(v => v.id === forecastForm.vertical_id)?.name;
      const skusInVertical = skus.filter(s => 
        s.vertical_id === forecastForm.vertical_id || s.vertical === verticalName
      );
      
      // Get unique brand names from these SKUs
      const brandNamesFromSkus = [...new Set(skusInVertical.map(s => s.brand).filter(Boolean))];
      
      // Match with brands collection or create virtual brands
      filteredBrands = brands.filter(b => brandNamesFromSkus.includes(b.name));
      
      // If no brands found in collection, create from SKU brand names
      if (filteredBrands.length === 0) {
        filteredBrands = brandNamesFromSkus.map(name => ({
          id: name,
          name: name
        }));
      }
    }
    
    // If model is selected, further filter by model
    if (forecastForm.model_id) {
      const modelName = models.find(m => m.id === forecastForm.model_id)?.name;
      const skusInModel = skus.filter(s => 
        s.model_id === forecastForm.model_id || s.model === modelName
      );
      
      const brandNamesFromSkus = [...new Set(skusInModel.map(s => s.brand).filter(Boolean))];
      filteredBrands = filteredBrands.filter(b => brandNamesFromSkus.includes(b.name));
      
      // If no brands found, create from SKU brand names
      if (filteredBrands.length === 0) {
        filteredBrands = brandNamesFromSkus.map(name => ({
          id: name,
          name: name
        }));
      }
    }
    
    return filteredBrands;
  };

  const getFilteredSkus = () => {
    let filtered = skus;
    
    if (forecastForm.vertical_id) {
      const verticalName = verticals.find(v => v.id === forecastForm.vertical_id)?.name;
      filtered = filtered.filter(s => 
        s.vertical_id === forecastForm.vertical_id || 
        s.vertical === verticalName
      );
    }
    
    if (forecastForm.model_id) {
      const modelName = models.find(m => m.id === forecastForm.model_id)?.name;
      filtered = filtered.filter(s => 
        s.model_id === forecastForm.model_id || 
        s.model === modelName
      );
    }
    
    if (forecastForm.brand_id) {
      const brandName = brands.find(b => b.id === forecastForm.brand_id)?.name;
      filtered = filtered.filter(s => 
        s.brand_id === forecastForm.brand_id || 
        s.brand === brandName
      );
    }
    
    return filtered;
  };

  // Get filtered forecasts — server already applies buyer/vertical/
  // month + SKU search filters, so the frontend just returns the
  // paginated slice as-is. The Feb 2026 R14+ (AC10) refactor moved
  // the sku_id search to the server so we no longer paint over
  // partial results at page boundaries.
  const getFilteredForecasts = () => forecasts;

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setForecastFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Get models filtered for the forecast filter dropdown
  const getFilterModels = () => {
    if (!forecastFilters.vertical_id) return models;
    return models.filter(m => m.vertical_id === forecastFilters.vertical_id);
  };

  const handleCreateForecast = async () => {
    // Validate buyer is required
    if (!forecastForm.buyer_id) {
      toast.error("Buyer is required for creating a forecast");
      return;
    }
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Feb 2026 R14+ (AC2) — quantity=0 is now valid (an intentional
      // audited zero-out). Only strictly-negative values are rejected.
      if (forecastForm.quantity < 0) {
        toast.error("Quantity cannot be negative");
        return;
      }
      
      if (!forecastForm.sku_id) {
        toast.error("Please select a SKU");
        return;
      }

      // R5 (Feb 2026): on edit, require a non-trivial Reason (>= 3 chars)
      // when the user is reducing the forecast quantity. Upward / no-op
      // revisions remain reason-optional (matches R4 behaviour).
      const isEdit = !!forecastForm.id;
      const isDownward =
        isEdit &&
        originalEditQuantity != null &&
        forecastForm.quantity < originalEditQuantity;
      if (isDownward && (forecastForm.reason || "").trim().length < 3) {
        toast.error("Reason (min 3 characters) is required when reducing forecast quantity");
        return;
      }
      
      const payload = {
        buyer_id: forecastForm.buyer_id,  // REQUIRED
        // Feb 2026 R14+ (AC9) — UTC-safe first-of-month builder. Fixes
        // the browser-timezone off-by-one where "2026-08-01".toISOString()
        // in IST (+05:30) rolled BACK to 2026-07-31T18:30Z, reading
        // back as July.
        forecast_month: buildFirstOfMonthISO(forecastForm.forecast_month),
        quantity: forecastForm.quantity,
        priority: forecastForm.priority,
        notes: forecastForm.notes || ""
      };

      if (forecastForm.vertical_id) payload.vertical_id = forecastForm.vertical_id;
      if (forecastForm.sku_id) payload.sku_id = forecastForm.sku_id;
      // R4 (Feb 2026): only attach `reason` on UPDATE — creates have no audit row.
      if (forecastForm.id && forecastForm.reason) payload.reason = forecastForm.reason;
      
      if (forecastForm.id) {
        // Update existing forecast
        await axios.put(`${API}/forecasts/${forecastForm.id}`, payload, { headers });
        toast.success("Forecast updated");
      } else {
        // Feb 2026 R14+ (user-clarified) — the create path is removed.
        // Bulk upload is the only entry point for forecast set changes.
        toast.error("Forecast creation is only available via Bulk Upload.");
        return;
      }
      
      setShowForecastDialog(false);
      resetForecastForm();
      fetchAllData();
    } catch (error) {
      // R13 (Jul 2026) — the ForecastLocked 409 path is gone; edits are
      // always accepted server-side (subject only to standard permission
      // and quantity>0 validation).
      toast.error(error.response?.data?.detail || "Failed to save forecast");
    }
  };

  const resetForecastForm = () => {
    setForecastForm({
      id: null,
      buyer_id: "",
      vertical_id: "",
      model_id: "",
      brand_id: "",
      sku_id: "",
      forecast_month: new Date().toISOString().slice(0, 7),
      quantity: 0,
      priority: "MEDIUM",
      notes: "",
      reason: ""
    });
    // R13 (Jul 2026) — lock state is gone; only clear the R4 baseline
    // quantity used by the Reason-required-on-downward gate.
    setOriginalEditQuantity(null);
  };

  // R13 (Jul 2026) — Removed handlers: handleConfirmForecast,
  // handleSelectForecast, handleSelectAllDraft, handleBulkConfirm,
  // handleConfirmMonth. CONFIRMED status is no longer part of the
  // forecast lifecycle, and the confirm endpoints (per-forecast, bulk,
  // month) have been deleted server-side.

  // Edit forecast - open dialog with existing data
  const handleEditForecast = async (forecast) => {
    setForecastForm({
      buyer_id: forecast.buyer_id || "",
      vertical_id: forecast.vertical_id || "",
      model_id: "",
      brand_id: "",
      sku_id: forecast.sku_id || "",
      forecast_month: forecast.forecast_month ? 
        (typeof forecast.forecast_month === 'string' ? forecast.forecast_month.slice(0, 7) : forecast.forecast_month.toISOString().slice(0, 7)) 
        : new Date().toISOString().slice(0, 7),
      quantity: forecast.quantity || 0,
      priority: forecast.priority || "MEDIUM",
      notes: forecast.notes || "",
      // R4 (Feb 2026): reason is always blank on open — it's per-edit, not stored on the doc.
      reason: "",
      // Store ID for update
      id: forecast.id
    });
    setOriginalEditQuantity(forecast.quantity || 0);
    setShowForecastDialog(true);
    // R13 (Jul 2026) — no lock-state fetch. Edit dialog opens without any
    // server-side gate; the Reason-required-on-downward UX is enforced
    // client-side using `originalEditQuantity` above.
  };

  // R3+R4 (Feb 2026): open the Details dialog for a forecast — fetch the
  // linked production schedules (sorted by created_at ASC server-side) and
  // display them alongside the forecast's `version_history[]`.
  const handleViewForecastDetails = async (forecast) => {
    setDetailsForecast(forecast);
    setDetailsSchedules([]);
    setDetailsPlanStatuses(null);
    setDetailsLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [schedRes, statusRes] = await Promise.all([
        axios.get(`${API}/production-schedules`, { params: { forecast_id: forecast.id }, headers }),
        axios.get(`${API}/forecasts/${forecast.id}/plan-block-statuses`, { headers }),
      ]);
      setDetailsSchedules(Array.isArray(schedRes.data) ? schedRes.data : []);
      setDetailsPlanStatuses(statusRes.data || null);
    } catch (e) {
      console.error("Failed to fetch production schedules for forecast", e);
      setDetailsSchedules([]);
      setDetailsPlanStatuses(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Delete forecast
  const handleDeleteForecast = async (id) => {
    if (!window.confirm("Are you sure you want to delete this forecast?")) {
      return;
    }
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/forecasts/${id}`, { headers });
      toast.success("Forecast deleted");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete forecast");
    }
  };

  // Get dispatch lots for a forecast
  const fetchForecastDispatchLots = async (forecastId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/forecasts/${forecastId}/dispatch-lots`, { headers });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch dispatch lots:", error);
      return [];
    }
  };

  // Bulk upload handlers
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error("Please upload an Excel file (.xlsx or .xls)");
      // Reset file input to allow re-selecting the same file
      e.target.value = '';
      return;
    }
    
    parseExcelFile(file);
    // Reset file input to allow re-selecting the same file for re-upload
    e.target.value = '';
  };

  const parseExcelFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/forecasts/parse-excel`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });

      setUploadPreview(response.data.forecasts || []);
      setUploadErrors(response.data.errors || []);

      // Feb 2026 R14+ (AC1, AC8) — rebuild the file into the R14+
      // canonical shape (SKU_ID / CUSTOMER_CODE / MONTH /
      // PRIORITY_BUCKET / QUANTITY / REASON) once, cache it, and
      // reuse for BOTH /bulk-upload/preview (impact dialog) AND
      // /bulk-upload (commit). Guarantees the same bytes drive both
      // endpoints — no schema drift between preview and commit.
      // Feb 2026 (BUG-FORECAST-TEMPLATE-CONSOLIDATION follow-up) —
      // PRIORITY_BUCKET added to the canonical shape so the parse-
      // and-canonicalise workflow no longer strips it before the
      // preview / commit calls.
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();
      const rows = (response.data.forecasts || []).map((r) => ({
        SKU_ID: r.sku_id,
        CUSTOMER_CODE: r.customer_code || "",
        MONTH: r.month,
        PRIORITY_BUCKET: r.priority_bucket || "",
        QUANTITY: r.quantity,
        REASON: "",
      }));
      const ws = XLSX.utils.json_to_sheet(rows, {
        header: ["SKU_ID", "CUSTOMER_CODE", "MONTH", "PRIORITY_BUCKET", "QUANTITY", "REASON"],
      });
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
      const canonicalBlob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      setPendingUploadFile(canonicalBlob);

      try {
        const previewFormData = new FormData();
        previewFormData.append('file', canonicalBlob, "preview.xlsx");
        const previewRes = await axios.post(
          `${API}/forecasts/bulk-upload/preview`,
          previewFormData,
          { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
        );
        setPreviewImpact(previewRes.data);
      } catch (previewErr) {
        // Preview is best-effort — if it fails, we still show the row
        // list and let the commit endpoint be the authoritative gate.
        console.warn("bulk-upload preview failed", previewErr);
        setPreviewImpact(null);
      }

      setShowUploadDialog(true);

      if (response.data.error_count > 0) {
        toast.warning(`${response.data.error_count} rows have errors and will be skipped`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to parse Excel file");
    }
  };

  const handleBulkUpload = async () => {
    // Feb 2026 R14+ (AC1, AC2, AC3, AC5, AC8) — commit path routes
    // through `/forecasts/bulk-upload` (single-shot version-replace
    // upload). Previously the frontend looped per-row against
    // `POST /forecasts` which produced ADDITIONS not a VERSIONED
    // REPLACEMENT of the buyer/month set.
    if (!pendingUploadFile) {
      toast.error("No file to upload — re-select the Excel file");
      return;
    }
    setUploading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const formData = new FormData();
      formData.append('file', pendingUploadFile, "forecasts.xlsx");
      const res = await axios.post(`${API}/forecasts/bulk-upload`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      const {
        created_count = 0,
        edited_count = 0,
        unchanged_count = 0,
        safety_net_zeroed_count = 0,
        buyer_month_pairs_versioned = 0,
        dispatch_lots_relinked = 0,
        dispatch_lot_lines_relinked = 0,
        production_schedules_relinked = 0,
        error_count = 0,
      } = res.data || {};
      const msg = [
        `${created_count + edited_count + unchanged_count} rows applied`,
        `${buyer_month_pairs_versioned} buyer/month set${buyer_month_pairs_versioned === 1 ? '' : 's'} versioned`,
        safety_net_zeroed_count > 0 ? `${safety_net_zeroed_count} safety-net zeroed` : null,
        (dispatch_lots_relinked + dispatch_lot_lines_relinked) > 0
          ? `${dispatch_lots_relinked + dispatch_lot_lines_relinked} dispatch link${(dispatch_lots_relinked + dispatch_lot_lines_relinked) === 1 ? '' : 's'} re-mapped`
          : null,
        production_schedules_relinked > 0
          ? `${production_schedules_relinked} schedule${production_schedules_relinked === 1 ? '' : 's'} re-mapped`
          : null,
        error_count > 0 ? `${error_count} rows skipped` : null,
      ].filter(Boolean).join(' · ');
      toast.success(msg);

      setShowUploadDialog(false);
      setUploadPreview([]);
      setUploadErrors([]);
      setPreviewImpact(null);
      setPendingUploadFile(null);
      fetchAllData();
    } catch (error) {
      toast.error("Bulk upload failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const downloadErrorReport = async () => {
    if (uploadErrors.length === 0) return;

    setDownloadingErrors(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/forecasts/generate-error-report`,
        uploadErrors,
        {
          headers: { ...headers, 'Content-Type': 'application/json' },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'forecast_upload_errors.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Error report downloaded");
    } catch (error) {
      toast.error("Failed to download error report");
    } finally {
      setDownloadingErrors(false);
    }
  };

  // Feb 2026 (BULK-UPLOAD-OVERFLOW) — Aggregate impact numbers for the
  // one-line summary that replaces the inline per-buyer card stack.
  // Recomputed only when previewImpact.plans changes.
  const impactSummary = useMemo(() => {
    const plans = (previewImpact && previewImpact.plans) || [];
    return {
      versionedPairs: plans.length,
      safetyNetCount: plans.reduce((n, p) => n + ((p.safety_net_skus || []).length), 0),
      dispatchLotsCount: plans.reduce((n, p) => n + ((p.affected_dispatch_lot_codes || []).length), 0),
      schedulesCount: plans.reduce((n, p) => n + ((p.affected_schedule_codes || []).length), 0),
    };
  }, [previewImpact]);

  // Feb 2026 (BULK-UPLOAD-OVERFLOW) — Client-side CSV of the full
  // per-buyer impact detail. Reuses the exact `plans[]` shape the
  // inline list used to render, so nothing new fetches from the
  // backend. Called once per new previewImpact via the effect below.
  const downloadImpactReport = (impact) => {
    const plans = (impact && impact.plans) || [];
    if (plans.length === 0) return;
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = [
      "buyer_name", "buyer_id", "month_key",
      "current_version", "new_version", "uploaded_sku_count",
      "safety_net_skus", "affected_dispatch_lot_codes",
      "affected_schedule_codes",
    ];
    const rows = plans.map((p) => [
      p.buyer_name || "",
      p.buyer_id || "",
      p.month_key || "",
      p.current_version ?? "",
      p.new_version ?? "",
      p.uploaded_sku_count ?? "",
      (p.safety_net_skus || []).join(";"),
      (p.affected_dispatch_lot_codes || []).join(";"),
      (p.affected_schedule_codes || []).join(";"),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map(esc).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    link.setAttribute("download", `forecast_upload_impact_${stamp}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // Effect: fires ONLY when `previewImpact` reference changes (i.e. a
  // new file is previewed). React 18 StrictMode double-invokes effects
  // in dev, so we guard on a ref that tracks the last-downloaded
  // previewImpact object identity — never double-downloads for the
  // same object.
  const lastDownloadedImpactRef = useRef(null);
  useEffect(() => {
    if (
      previewImpact
      && previewImpact.plans
      && previewImpact.plans.length > 0
      && lastDownloadedImpactRef.current !== previewImpact
    ) {
      lastDownloadedImpactRef.current = previewImpact;
      downloadImpactReport(previewImpact);
    }
  }, [previewImpact]);


  // Feb 2026 (BUG-FORECAST-TEMPLATE-CONSOLIDATION) — Previously this
  // built the template client-side with SheetJS, which drifted from
  // the backend validator's header contract and shipped a broken
  // 4-column file to prod. Now delegates to
  // `GET /forecasts/bulk-upload/template` — single source of truth
  // for headers, sample rows, instructions, AND the Buyer Master
  // reference sheet (all three now live in one place, in Python).
  const downloadTemplate = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(
        `${API}/forecasts/bulk-upload/template`,
        { headers, responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'forecasts_bulk_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Forecast upload template downloaded");
    } catch (error) {
      const status = error?.response?.status;
      if (status === 403) {
        toast.error("Not authorised to download the template (needs Forecast:CREATE).");
      } else {
        toast.error(`Failed to download template${status ? ` (HTTP ${status})` : ''}`);
      }
      console.error("Template download error:", error);
    }
  };

  // Export forecasts with filters
  const handleExportForecasts = async () => {
    setExporting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Build query params
      const params = new URLSearchParams();
      if (exportFilters.startDate) params.append('start_date', new Date(exportFilters.startDate + '-01').toISOString());
      if (exportFilters.endDate) params.append('end_date', new Date(exportFilters.endDate + '-28').toISOString());
      if (exportFilters.buyer_id && exportFilters.buyer_id !== '_all') params.append('buyer_id', exportFilters.buyer_id);
      if (exportFilters.brand && exportFilters.brand !== '_all') params.append('brand', exportFilters.brand);
      if (exportFilters.model && exportFilters.model !== '_all') params.append('model', exportFilters.model);
      if (exportFilters.status && exportFilters.status !== '_all') params.append('status', exportFilters.status);
      
      const response = await axios.get(`${API}/forecasts/export?${params.toString()}`, {
        headers,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `forecast_export_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Forecast data exported successfully");
      setShowExportDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to export forecasts");
    } finally {
      setExporting(false);
    }
  };

  // Get unique brands from SKUs for export filter
  const getUniqueBrands = () => {
    const brandSet = new Set(skus.map(s => s.brand).filter(Boolean));
    return Array.from(brandSet).sort();
  };

  // Get unique models from SKUs for export filter
  const getUniqueModels = () => {
    const modelSet = new Set(skus.map(s => s.model).filter(Boolean));
    return Array.from(modelSet).sort();
  };

  const getBuyerName = (id) => buyers.find(b => b.id === id)?.name || id || '-';
  const getVerticalName = (id) => verticals.find(v => v.id === id)?.name || id || '-';
  const getSkuDescription = (skuId) => skus.find(s => s.sku_id === skuId)?.description || skuId;
  
  // Helper function to get auth headers
  const getHeaders = () => token ? { Authorization: `Bearer ${token}` } : {};
  
  // Get vertical name - from forecast's vertical_id or from SKU's vertical
  const getVerticalDisplay = (forecast) => {
    if (forecast.vertical_id) {
      const v = verticals.find(v => v.id === forecast.vertical_id);
      if (v) return v.name;
    }
    // Fallback to SKU's vertical
    if (forecast.sku_id) {
      const sku = skus.find(s => s.sku_id === forecast.sku_id);
      if (sku?.vertical) return sku.vertical;
      if (sku?.vertical_id) {
        const v = verticals.find(v => v.id === sku.vertical_id);
        if (v) return v.name;
      }
    }
    return '-';
  };
  
  // Get brand name from SKU
  const getBrandDisplay = (forecast) => {
    if (forecast.sku_id) {
      const sku = skus.find(s => s.sku_id === forecast.sku_id);
      if (sku?.brand) return sku.brand;
    }
    return '-';
  };
  
  // Get model name from SKU
  const getModelDisplay = (forecast) => {
    if (forecast.sku_id) {
      const sku = skus.find(s => s.sku_id === forecast.sku_id);
      if (sku?.model) return sku.model;
    }
    return '-';
  };

  const getStatusColor = (status) => {
    // R13 (Jul 2026) — CONFIRMED removed from the lifecycle so its blue
    // colour class is gone. DRAFT (resting) and CONVERTED (schedule-
    // linked) are the two live states. CANCELLED / EXPIRED are reserved
    // for future admin workflows.
    const colors = {
      'DRAFT': 'bg-zinc-100 text-zinc-700 border-zinc-300',
      'CONVERTED': 'bg-green-100 text-green-700 border-green-300',
      'CANCELLED': 'bg-red-100 text-red-700 border-red-300',
      'EXPIRED': 'bg-amber-100 text-amber-700 border-amber-300'
    };
    return colors[status] || 'bg-zinc-100 text-zinc-700 border-zinc-300';
  };

  return (
    <div className="p-6 md:p-8" data-testid="demand-page">
      <div className="mb-8">
        <h1 className="text-4xl font-black tracking-tight uppercase">Demand Forecasts</h1>
        <p className="text-sm text-muted-foreground mt-1 font-mono">Forecast entry, BOM costing & bulk upload</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="forecasts" className="uppercase text-xs tracking-wide" data-testid="tab-forecasts">
            <TrendingUp className="w-4 h-4 mr-2" />
            Forecasts
          </TabsTrigger>
          <TabsTrigger value="production-summary" className="uppercase text-xs tracking-wide" data-testid="tab-production-summary">
            <Factory className="w-4 h-4 mr-2" />
            Production Summary
          </TabsTrigger>
          <TabsTrigger value="forecast-summary" className="uppercase text-xs tracking-wide" data-testid="tab-forecast-summary">
            <TrendingUp className="w-4 h-4 mr-2" />
            Forecast Summary
          </TabsTrigger>
        </TabsList>

        {/* Forecasts Tab */}
        <TabsContent value="forecasts">
          <div className="flex justify-between items-center mb-4 gap-4">
            <h2 className="text-lg font-bold">Demand Forecasts</h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={downloadTemplate} className="text-xs">
                <Download className="w-4 h-4 mr-2" />
                Template
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowExportDialog(true)}
                className="text-xs bg-blue-50 border-blue-200 hover:bg-blue-100"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Export Forecasts
              </Button>
              <Button 
                variant="outline" 
                onClick={() => fileInputRef.current?.click()}
                className="text-xs"
              >
                <Upload className="w-4 h-4 mr-2" />
                Bulk Upload
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="hidden"
              />
              {/* Feb 2026 R14+ (user-clarified) — the standalone
                  "New Forecast" add button + its `POST /forecasts`
                  endpoint have been removed. Bulk upload is now the
                  only entry point for forecast set changes. The
                  Dialog below is retained but only opens on EDIT
                  (via `openEditForecast`), never for a fresh create. */}
              <Dialog open={showForecastDialog} onOpenChange={(open) => { setShowForecastDialog(open); if (!open) resetForecastForm(); }}>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2" data-testid="forecast-dialog-title">
                      <span>Edit Forecast</span>
                      {/* R13 (Jul 2026) — LOCKED pill removed. Edits are
                          always allowed. */}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                    {/* R13 (Jul 2026) — Downward-revision lock banner removed. */}
                    {/* Buyer - REQUIRED */}
                    <div>
                      <Label>Buyer *</Label>
                      <Select 
                        value={forecastForm.buyer_id || "_none"} 
                        onValueChange={(v) => setForecastForm({...forecastForm, buyer_id: v === "_none" ? "" : v})}
                      >
                        <SelectTrigger className={!forecastForm.buyer_id ? "border-red-300" : ""}>
                          <SelectValue placeholder="Select buyer (required)" />
                        </SelectTrigger>
                        <SelectContent>
                          {buyers.filter(b => b.id).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {!forecastForm.buyer_id && (
                        <p className="text-xs text-red-500 mt-1">Buyer is required</p>
                      )}
                    </div>

                    {/* Vertical */}
                    <div>
                      <Label>Vertical *</Label>
                      <Select 
                        value={forecastForm.vertical_id || "_none"} 
                        onValueChange={(v) => setForecastForm({
                          ...forecastForm, 
                          vertical_id: v === "_none" ? "" : v,
                          model_id: "",
                          brand_id: "",
                          sku_id: ""
                        })}
                      >
                        <SelectTrigger><SelectValue placeholder="Select vertical" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">All Verticals</SelectItem>
                          {getFilteredVerticals().filter(v => v.id).map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Model - cascaded from Vertical */}
                    <div>
                      <Label>Model</Label>
                      <Select 
                        value={forecastForm.model_id || "_none"} 
                        onValueChange={(v) => setForecastForm({
                          ...forecastForm, 
                          model_id: v === "_none" ? "" : v,
                          brand_id: "",
                          sku_id: ""
                        })}
                      >
                        <SelectTrigger><SelectValue placeholder="Select model (optional)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">All Models</SelectItem>
                          {getFilteredModels().filter(m => m.id).map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Brand - cascaded from Model */}
                    <div>
                      <Label>Brand</Label>
                      <Select 
                        value={forecastForm.brand_id || "_none"} 
                        onValueChange={(v) => setForecastForm({
                          ...forecastForm, 
                          brand_id: v === "_none" ? "" : v,
                          sku_id: ""
                        })}
                      >
                        <SelectTrigger><SelectValue placeholder="Select brand (optional)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">All Brands</SelectItem>
                          {getFilteredBrands().filter(b => b.id).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* SKU - filtered by above selections */}
                    <div>
                      <Label>SKU (Optional - for SKU-level forecast)</Label>
                      <Select 
                        value={forecastForm.sku_id || "_all"} 
                        onValueChange={(v) => setForecastForm({...forecastForm, sku_id: v === "_all" ? "" : v})}
                      >
                        <SelectTrigger><SelectValue placeholder="All filtered SKUs" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">All SKUs (Vertical-level forecast)</SelectItem>
                          {getFilteredSkus().filter(s => s.sku_id).slice(0, 200).map(s => (
                            <SelectItem key={s.sku_id} value={s.sku_id}>
                              {s.sku_id} - {s.description?.slice(0, 25)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-zinc-500 mt-1">
                        {getFilteredSkus().length} SKUs match current filters
                      </p>
                    </div>

                    {/* Forecast Month */}
                    <div>
                      <Label>Forecast Month *</Label>
                      <Input 
                        type="month" 
                        value={forecastForm.forecast_month}
                        onChange={(e) => setForecastForm({...forecastForm, forecast_month: e.target.value})}
                      />
                    </div>

                    {/* Quantity */}
                    <div>
                      <Label>Quantity * (must be greater than 0)</Label>
                      <Input 
                        type="number"
                        min="1"
                        value={forecastForm.quantity}
                        onChange={(e) => setForecastForm({...forecastForm, quantity: parseInt(e.target.value) || 0})}
                        className={forecastForm.quantity <= 0 ? "border-red-300" : ""}
                      />
                      {forecastForm.quantity <= 0 && (
                        <p className="text-xs text-red-500 mt-1">Quantity must be greater than 0</p>
                      )}
                    </div>

                    {/* Priority */}
                    <div>
                      <Label>Priority</Label>
                      <Select value={forecastForm.priority} onValueChange={(v) => setForecastForm({...forecastForm, priority: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="LOW">Low</SelectItem>
                          <SelectItem value="MEDIUM">Medium</SelectItem>
                          <SelectItem value="HIGH">High</SelectItem>
                          <SelectItem value="CRITICAL">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* R4+R5 (Feb 2026): Reason field — required (min 3 chars)
                        when the user reduces quantity (downward revision).
                        Empty allowed for upward or same-quantity edits. */}
                    {forecastForm.id && (() => {
                      const isDownward =
                        originalEditQuantity != null &&
                        forecastForm.quantity < originalEditQuantity;
                      const reasonOk = (forecastForm.reason || "").trim().length >= 3;
                      const showReasonError = isDownward && !reasonOk;
                      return (
                        <div>
                          <Label>
                            Reason for change{" "}
                            {isDownward ? (
                              <span className="text-red-600 font-normal">(required for downward revision)</span>
                            ) : (
                              <span className="text-zinc-400 font-normal">(optional)</span>
                            )}
                          </Label>
                          <Input
                            type="text"
                            placeholder="e.g. Customer revised PO, pushed delivery by 2 weeks…"
                            value={forecastForm.reason}
                            onChange={(e) => setForecastForm({ ...forecastForm, reason: e.target.value })}
                            className={showReasonError ? "border-red-300" : ""}
                            data-testid="forecast-edit-reason-input"
                          />
                          {showReasonError ? (
                            <p className="text-xs text-red-500 mt-1">
                              Reason must be at least 3 characters when reducing forecast quantity.
                            </p>
                          ) : (
                            <p className="text-xs text-zinc-500 mt-1">
                              Captured in this forecast&apos;s version history. Logged only when quantity or month changes.
                            </p>
                          )}
                        </div>
                      );
                    })()}

                    <Button 
                      onClick={handleCreateForecast} 
                      className="w-full"
                      disabled={
                        // Feb 2026 R14+ (user-clarified) — this dialog
                        // is EDIT-ONLY now; the button 400s if the
                        // form is opened without an `id`.
                        !forecastForm.id ||
                        forecastForm.quantity < 0 ||
                        !forecastForm.buyer_id ||
                        // R5: disable when an edit is downward AND no valid reason yet.
                        (
                          forecastForm.id &&
                          originalEditQuantity != null &&
                          forecastForm.quantity < originalEditQuantity &&
                          (forecastForm.reason || "").trim().length < 3
                        )
                      }
                      data-testid="forecast-submit-btn"
                    >
                      Update Forecast
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
          
          {/* Filters Bar */}
          <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-zinc-50 border rounded-lg">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-zinc-400" />
              <span className="text-xs font-medium text-zinc-500 uppercase">Filters:</span>
            </div>

            {/* Month Filter — defaults to current YYYY-MM on page load
                (Feb 2026 request). Clearing the value = all months. */}
            <div className="flex items-center gap-1">
              <span className="text-xs text-zinc-500">Month:</span>
              <Input
                type="month"
                value={forecastFilters.month}
                onChange={(e) => { handleFilterChange('month', e.target.value); setCurrentPage(1); }}
                className="h-9 w-[150px] text-xs"
                data-testid="forecast-month-filter"
              />
            </div>
            
            {/* Customer/Buyer Filter */}
            <Select 
              value={forecastFilters.buyer_id || "_all"} 
              onValueChange={(v) => handleFilterChange('buyer_id', v === "_all" ? "" : v)}
            >
              <SelectTrigger className="w-[180px] h-9 text-xs" data-testid="forecast-buyer-filter">
                <SelectValue placeholder="All Customers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All Customers</SelectItem>
                {buyers.filter(b => b.id).map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Vertical Filter */}
            <Select 
              value={forecastFilters.vertical_id || "_all"} 
              onValueChange={(v) => {
                handleFilterChange('vertical_id', v === "_all" ? "" : v);
                setForecastFilters(prev => ({ ...prev, model_id: "" })); // Reset model when vertical changes
              }}
            >
              <SelectTrigger className="w-[150px] h-9 text-xs" data-testid="forecast-vertical-filter">
                <SelectValue placeholder="All Verticals" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All Verticals</SelectItem>
                {verticals.filter(v => v.id).map(v => (
                  <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Brand Filter */}
            <Select 
              value={forecastFilters.brand_id || "_all"} 
              onValueChange={(v) => handleFilterChange('brand_id', v === "_all" ? "" : v)}
            >
              <SelectTrigger className="w-[150px] h-9 text-xs" data-testid="forecast-brand-filter">
                <SelectValue placeholder="All Brands" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All Brands</SelectItem>
                {brands.filter(b => b.id).map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Model Filter */}
            <Select 
              value={forecastFilters.model_id || "_all"} 
              onValueChange={(v) => handleFilterChange('model_id', v === "_all" ? "" : v)}
            >
              <SelectTrigger className="w-[150px] h-9 text-xs" data-testid="forecast-model-filter">
                <SelectValue placeholder="All Models" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All Models</SelectItem>
                {getFilterModels().filter(m => m.id).map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Feb 2026 R14++ (AC3) — Quantity filter (Zero/Non-zero/All).
                Composes with month/buyer/SKU-search via the shared
                query builder. Defaults to Non-zero on page load. */}
            <Select
              value={forecastFilters.quantity_filter || "nonzero"}
              onValueChange={(v) => { handleFilterChange('quantity_filter', v); setCurrentPage(1); }}
            >
              <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="forecast-quantity-filter">
                <SelectValue placeholder="Non-zero" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nonzero" data-testid="forecast-quantity-filter-nonzero">Non-zero</SelectItem>
                <SelectItem value="zero" data-testid="forecast-quantity-filter-zero">Zero</SelectItem>
                <SelectItem value="all" data-testid="forecast-quantity-filter-all">All</SelectItem>
              </SelectContent>
            </Select>

            {/* Feb 2026 R14+ (AC10) — SKU search. Server-side match on
                both `forecast_code` and `sku_id`, so it covers the FULL
                filtered dataset (not just the currently-loaded page).
                Resets the pagination cursor on every submit so page-N
                mid-search doesn't leak into the next query. */}
            <BuyerSkuSearchBox
              value={forecastSkuSearch}
              onSubmit={(v) => { setForecastSkuSearch(v); setCurrentPage(1); }}
              onClear={() => { setForecastSkuSearch(""); setCurrentPage(1); }}
              testId="forecast-sku-search"
            />
            
            {/* Clear Filters */}
            {(forecastFilters.buyer_id || forecastFilters.vertical_id || forecastFilters.brand_id || forecastFilters.model_id || forecastFilters.month || forecastSkuSearch || (forecastFilters.quantity_filter && forecastFilters.quantity_filter !== "nonzero")) && (
              <Button 
                variant="ghost" 
                size="sm"
                className="text-xs h-9"
                onClick={() => { setForecastFilters({ buyer_id: "", vertical_id: "", brand_id: "", model_id: "", month: "", quantity_filter: "nonzero" }); setForecastSkuSearch(""); setCurrentPage(1); }}
              >
                <X className="w-3 h-3 mr-1" />
                Clear
              </Button>
            )}
            
            {/* Results count */}
            <span className="text-xs text-zinc-500 ml-auto">
              Showing {getFilteredForecasts().length} of {totalItems} forecasts
            </span>
          </div>
          
          {/* R13 (Jul 2026) — Bulk Confirmation Bar removed alongside the
              CONFIRMED status. Forecasts are always live; no sign-off step. */}

          {/* Feb 2026 — Filter-reactive summary cards. Values reflect
              the SAME filter set as the table (month + buyer + vertical
              + brand + model + non-zero quantity), computed server-side.
              Typing in the SKU search box narrows the table but leaves
              the cards stable — the backend explicitly excludes `search`
              from the summary aggregation (AC10). */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4" data-testid="forecasts-summary-cards">
            <div className="bg-white border rounded-lg p-4" data-testid="forecasts-card-total-qty">
              <div className="text-2xl font-bold" data-testid="forecasts-card-total-qty-value">
                {(forecastsSummary?.total_quantity || 0).toLocaleString('en-IN')}
              </div>
              <div className="text-xs text-muted-foreground uppercase">Total Forecast Quantity</div>
            </div>
            <div className="bg-white border rounded-lg p-4" data-testid="forecasts-card-buyer-skus">
              <div className="text-2xl font-bold" data-testid="forecasts-card-buyer-skus-value">
                {(forecastsSummary?.unique_buyer_skus || 0).toLocaleString('en-IN')}
              </div>
              <div className="text-xs text-muted-foreground uppercase">Buyer SKUs</div>
            </div>
            <div className="bg-white border rounded-lg p-4" data-testid="forecasts-card-buyers">
              <div className="text-2xl font-bold" data-testid="forecasts-card-buyers-value">
                {(forecastsSummary?.unique_buyers || 0).toLocaleString('en-IN')}
              </div>
              <div className="text-xs text-muted-foreground uppercase">Buyers</div>
            </div>
            <div className="bg-white border rounded-lg p-4" data-testid="forecasts-card-brands">
              <div className="text-2xl font-bold" data-testid="forecasts-card-brands-value">
                {(forecastsSummary?.unique_brands || 0).toLocaleString('en-IN')}
              </div>
              <div className="text-xs text-muted-foreground uppercase">Brands</div>
            </div>
          </div>

          {/* Forecasts Table */}
          <div className="border rounded-sm overflow-x-auto bg-white">
            <table className="w-full">
              <thead className="bg-zinc-50">
                <tr>
                  {/* R13 (Jul 2026) — MASTER_ADMIN-only checkbox column removed
                      alongside the bulk-confirm workflow. */}
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Forecast ID</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Month</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Buyer</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Vertical</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Brand</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Model</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">SKU</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Forecast Qty</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Dispatch Alloc</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Prod Scheduled</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Sched Pending</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Dispatch Lots</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Priority</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredForecasts().length === 0 ? (
                  <tr>
                    <td colSpan={14} className="p-8 text-center text-zinc-500">
                      No forecasts yet. Create one to get started.
                    </td>
                  </tr>
                ) : getFilteredForecasts().map((f) => (
                  <tr key={f.id} className="border-t hover:bg-zinc-50/50">
                    {/* R13 (Jul 2026) — per-row select checkbox removed. */}
                    <td className="p-4">
                      <button 
                        className="font-mono font-bold text-sm text-primary hover:underline cursor-pointer flex items-center gap-1"
                        onClick={async () => {
                          const lots = await fetchForecastDispatchLots(f.id);
                          setSelectedForecastLots({ forecast: f, lots: lots });
                          // Also fetch buyer's other lots for "Add to existing" option
                          // (V2 — see Feb 2026 fix: legacy `/dispatch-lots/by-buyer/{id}`
                          // returned 0 rows because V2 stores `customer_id` either as the
                          // buyer's UUID or as `customer_code` while the forecast holds the
                          // UUID. We now ask V2 for the lot list and match client-side
                          // against either alias).
                          if (f.buyer_id) {
                            try {
                              const buyer = buyers.find((b) => b.id === f.buyer_id);
                              const customerCode = buyer?.customer_code || null;
                              const buyerLotsRes = await axios.get(
                                `${API}/dispatch-lots-v2?page_size=200`,
                                { headers: getHeaders() },
                              );
                              const allLots = buyerLotsRes.data?.lots || [];
                              const filtered = allLots.filter((lot) => {
                                const matchesBuyer = (lot.customer_id && (lot.customer_id === f.buyer_id || lot.customer_id === customerCode))
                                  || (lot.buyer_id && lot.buyer_id === f.buyer_id);
                                const openStatus = ["DRAFT", "PENDING_FINANCE"].includes(lot.status);
                                return matchesBuyer && openStatus;
                              });
                              setBuyerExistingLots(filtered);
                            } catch (e) {
                              setBuyerExistingLots([]);
                            }
                          }
                          setShowLotsDialog(true);
                        }}
                        title="Click to view dispatch lots"
                      >
                        {f.forecast_code}
                        <Package className="w-3 h-3 opacity-50" />
                      </button>
                    </td>
                    <td className="p-4 font-mono text-sm">{f.forecast_month?.slice(0, 7)}</td>
                    <td className="p-4 text-sm">{getBuyerName(f.buyer_id)}</td>
                    <td className="p-4 text-sm">{getVerticalDisplay(f)}</td>
                    <td className="p-4 text-sm">{getBrandDisplay(f)}</td>
                    <td className="p-4 text-sm">{getModelDisplay(f)}</td>
                    <td className="p-4 font-mono text-sm text-zinc-600">{f.sku_id || 'All in Vertical'}</td>
                    <td className="p-4 font-mono font-bold">{f.quantity?.toLocaleString()}</td>
                    <td className="p-4 font-mono text-sm">
                      <span className={f.dispatch_allocated > 0 ? 'text-blue-600 font-medium' : 'text-zinc-400'}>
                        {(f.dispatch_allocated || 0).toLocaleString()}
                      </span>
                    </td>
                    <td className="p-4 font-mono text-sm">
                      <span className={f.production_scheduled > 0 ? 'text-green-600 font-medium' : 'text-zinc-400'}>
                        {(f.production_scheduled || 0).toLocaleString()}
                      </span>
                    </td>
                    <td className="p-4 font-mono text-sm">
                      <span className={f.schedule_pending > 0 ? 'text-orange-600 font-medium' : 'text-green-600'}>
                        {(f.schedule_pending || 0).toLocaleString()}
                      </span>
                    </td>
                    <td className="p-4">
                      <Button 
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={async () => {
                          const lots = await fetchForecastDispatchLots(f.id);
                          setSelectedForecastLots({ forecast: f, lots: lots });
                          if (f.buyer_id) {
                            try {
                              const buyer = buyers.find((b) => b.id === f.buyer_id);
                              const customerCode = buyer?.customer_code || null;
                              const buyerLotsRes = await axios.get(
                                `${API}/dispatch-lots-v2?page_size=200`,
                                { headers: getHeaders() },
                              );
                              const allLots = buyerLotsRes.data?.lots || [];
                              const filtered = allLots.filter((lot) => {
                                const matchesBuyer = (lot.customer_id && (lot.customer_id === f.buyer_id || lot.customer_id === customerCode))
                                  || (lot.buyer_id && lot.buyer_id === f.buyer_id);
                                const openStatus = ["DRAFT", "PENDING_FINANCE"].includes(lot.status);
                                return matchesBuyer && openStatus;
                              });
                              setBuyerExistingLots(filtered);
                            } catch (e) {
                              setBuyerExistingLots([]);
                            }
                          }
                          setShowLotsDialog(true);
                        }}
                      >
                        <Package className="w-3 h-3 mr-1" />
                        View Lots
                      </Button>
                    </td>
                    <td className="p-4">
                      {f.priority_bucket ? (
                        <span
                          data-testid={`forecast-priority-${f.id}`}
                          className={`text-xs font-mono px-2 py-1 rounded border ${
                            f.priority_bucket === 'P1' ? 'bg-red-100 text-red-700 border-red-300' :
                            f.priority_bucket === 'P2' ? 'bg-orange-100 text-orange-700 border-orange-300' :
                            f.priority_bucket === 'P3' ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
                            f.priority_bucket === 'P4' ? 'bg-zinc-100 text-zinc-700 border-zinc-300' :
                            'bg-zinc-100 border-zinc-300'
                          }`}
                        >
                          {f.priority_bucket}
                        </span>
                      ) : (
                        <span
                          data-testid={`forecast-priority-${f.id}-unset`}
                          className="text-xs font-mono px-2 py-1 rounded border bg-zinc-50 text-zinc-400 italic border-zinc-200"
                          title="No priority bucket set on this forecast"
                        >
                          —
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex gap-1">
                        {/* R3+R4 (Feb 2026): Details — shown for every forecast (DRAFT or CONFIRMED). View-only. */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewForecastDetails(f)}
                          title="View forecast version history & linked production schedules"
                          data-testid={`forecast-details-${f.id}`}
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                        {f.status === 'DRAFT' && (
                          <>
                            <PermButton entity="Forecast" action="UPDATE" size="sm" variant="ghost" onClick={() => handleEditForecast(f)} title="Edit" data-testid={`edit-forecast-${f.id}`}>
                              <Pencil className="w-4 h-4" />
                            </PermButton>
                            <PermButton entity="Forecast" action="DELETE" size="sm" variant="ghost" onClick={() => handleDeleteForecast(f.id)} title="Delete" className="text-red-600 hover:text-red-700" data-testid={`delete-forecast-${f.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </PermButton>
                          </>
                        )}
                        {/* R13 (Jul 2026) — per-row Confirm button removed;
                            CONFIRMED status no longer exists. */}
                      </div>
                    </td>
                  </tr>
                ))}
                {forecasts.length === 0 && (
                  <tr><td colSpan={16} className="p-8 text-center text-muted-foreground">{loading ? "Loading..." : "No forecasts yet. Create one to get started."}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            loading={loading}
          />
        </TabsContent>

        {/* ════════════════════════════════════════════════════════════
            R8 (Feb 2026) — Production Summary tab
            Aggregates per-forecast coverage across the full production
            schedule chain. Read-only; no drill-down (drill-down lives
            on the Forecasts tab via R3 Details dialog).
        ═══════════════════════════════════════════════════════════════*/}
        <TabsContent value="production-summary" data-testid="production-summary-content">
          {/* Header row — filters on the left, Export on the right. */}
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-xs">Month</Label>
                <Input
                  type="month"
                  value={psMonth}
                  onChange={(e) => setPsMonth(e.target.value)}
                  className="w-40"
                  data-testid="ps-month-input"
                />
              </div>
              <div>
                <Label className="text-xs">Customer</Label>
                <Select value={psBuyerId || "_all"} onValueChange={(v) => setPsBuyerId(v === "_all" ? "" : v)}>
                  <SelectTrigger className="w-60" data-testid="ps-buyer-select">
                    <SelectValue placeholder="All Customers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Customers</SelectItem>
                    {buyers.filter(b => b.id).map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Time</Label>
                <Select value={psTimeFilter || "_all"} onValueChange={(v) => setPsTimeFilter(v === "_all" ? "" : v)}>
                  <SelectTrigger className="w-36" data-testid="ps-time-filter">
                    <SelectValue placeholder="All Time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Time</SelectItem>
                    <SelectItem value="OVERDUE">Overdue</SelectItem>
                    <SelectItem value="ON_TRACK">On Track</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Quantity</Label>
                <Select value={psQtyFilter || "_all"} onValueChange={(v) => setPsQtyFilter(v === "_all" ? "" : v)}>
                  <SelectTrigger className="w-40" data-testid="ps-qty-filter">
                    <SelectValue placeholder="All Quantity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Quantity</SelectItem>
                    <SelectItem value="SHORTFALL">Shortfall</SelectItem>
                    <SelectItem value="EXCESS">Excess</SelectItem>
                    <SelectItem value="MATCHES_FORECAST">Matches Forecast</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Override</Label>
                <Select value={psOverrideFilter || "_all"} onValueChange={(v) => setPsOverrideFilter(v === "_all" ? "" : v)}>
                  <SelectTrigger className="w-40" data-testid="ps-override-filter">
                    <SelectValue placeholder="All Overrides" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Overrides</SelectItem>
                    <SelectItem value="NO_SCHEDULE">No Schedule</SelectItem>
                    <SelectItem value="COMPLETED">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Model</Label>
                <Select value={psModelFilter || "_all"} onValueChange={(v) => setPsModelFilter(v === "_all" ? "" : v)}>
                  <SelectTrigger className="w-40" data-testid="ps-model-filter">
                    <SelectValue placeholder="All Models" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Models</SelectItem>
                    {models.filter(m => m.id).map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Brand</Label>
                <Select value={psBrandFilter || "_all"} onValueChange={(v) => setPsBrandFilter(v === "_all" ? "" : v)}>
                  <SelectTrigger className="w-40" data-testid="ps-brand-filter">
                    <SelectValue placeholder="All Brands" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Brands</SelectItem>
                    {brands.filter(b => b.id).map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">SKU</Label>
                <BuyerSkuSearchBox
                  value={psSkuSearch}
                  onSubmit={setPsSkuSearch}
                  onClear={() => setPsSkuSearch("")}
                  testId="ps-sku-search"
                />
              </div>
              <div className="flex items-end pb-1">
                {/* Expired-toggle removed in the Jul 2026 shim cleanup
                    — the backend `?status=EXPIRED` filter was retired.
                    See state block above for the rationale. */}
              </div>
            </div>
            <Button
              variant="outline"
              onClick={exportProductionSummary}
              className="text-xs bg-blue-50 border-blue-200 hover:bg-blue-100"
              data-testid="ps-export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>

          {/* Filter chip bar — 7 pills wired to the flat parallel
              summary buckets. A row can count in MULTIPLE pills (its
              time bucket + its qty bucket, or its override bucket) so
              the pill counts intentionally don't sum to `total`. Click
              a pill to toggle the corresponding Time / Qty / Override
              filter select. */}
          <div
            className="flex flex-wrap gap-2 text-xs mb-4 p-3 rounded border bg-zinc-50"
            data-testid="ps-summary-bar"
          >
            <span className="text-zinc-700 mr-2 flex items-center">
              Showing <strong className="mx-1">{filteredPsRows.length}</strong> of{" "}
              <strong className="mx-1">{psSummary.total || 0}</strong> forecasts —{" "}
              <span className="text-zinc-500 italic ml-1">
                pills count each row across independent Time / Qty / Override buckets
              </span>
            </span>
            {[
              { k: "OVERDUE",          axis: "time",     count: psSummary.overdue,          color: "bg-red-100 text-red-800 border-red-300 hover:bg-red-200" },
              { k: "ON_TRACK",         axis: "time",     count: psSummary.on_track,         color: "bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200" },
              { k: "SHORTFALL",        axis: "qty",      count: psSummary.shortfall,        color: "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200" },
              { k: "EXCESS",           axis: "qty",      count: psSummary.excess,           color: "bg-violet-100 text-violet-800 border-violet-300 hover:bg-violet-200" },
              { k: "MATCHES_FORECAST", axis: "qty",      count: psSummary.matches_forecast, color: "bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200" },
              { k: "NO_SCHEDULE",      axis: "override", count: psSummary.no_schedule,      color: "bg-zinc-200 text-zinc-800 border-zinc-300 hover:bg-zinc-300" },
              { k: "COMPLETED",        axis: "override", count: psSummary.completed,        color: "bg-emerald-200 text-emerald-900 border-emerald-300 hover:bg-emerald-300" },
            ].map(({ k, axis, count, color }) => {
              const active =
                (axis === "time"     && psTimeFilter === k) ||
                (axis === "qty"      && psQtyFilter === k) ||
                (axis === "override" && psOverrideFilter === k);
              const toggle = () => {
                if (axis === "time")     setPsTimeFilter(active ? "" : k);
                if (axis === "qty")      setPsQtyFilter(active ? "" : k);
                if (axis === "override") setPsOverrideFilter(active ? "" : k);
              };
              return (
                <button
                  key={k}
                  type="button"
                  onClick={toggle}
                  className={`px-2.5 py-1 rounded border font-semibold tracking-wide transition-colors ${color} ${active ? "ring-2 ring-offset-1 ring-zinc-400" : ""}`}
                  data-testid={`ps-summary-chip-${k.toLowerCase()}`}
                >
                  {count || 0} {STATUS_BADGE_LABEL[k] || k}
                </button>
              );
            })}
            {(psTimeFilter || psQtyFilter || psOverrideFilter) && (
              <button
                type="button"
                onClick={() => { setPsTimeFilter(""); setPsQtyFilter(""); setPsOverrideFilter(""); }}
                className="px-2.5 py-1 rounded border bg-white text-zinc-700 hover:bg-zinc-100"
                data-testid="ps-clear-filter"
              >
                Clear filters
              </button>
            )}
          </div>

          {psLoading && (
            <div className="p-6 text-center text-sm text-zinc-500" data-testid="ps-loading">
              Loading production summary…
            </div>
          )}

          {!psLoading && filteredPsRows.length === 0 && (
            <div className="p-8 text-center text-sm text-zinc-500 border rounded bg-white" data-testid="ps-empty">
              <Factory className="w-8 h-8 mx-auto mb-2 text-zinc-400" />
              No forecasts match the current filters for {psMonth}.
            </div>
          )}

          {!psLoading && filteredPsRows.length > 0 && (
            <div className="overflow-x-auto border rounded bg-white">
              <table className="w-full text-sm" data-testid="ps-table">
                <thead className="bg-zinc-50 border-b">
                  {/* R8.2 (Feb 2026) — static header tooltips.
                      Uses shadcn `Tooltip` (Radix under the hood) — hover
                      on desktop, tap on touch. Copy is frozen per the
                      R8.2 spec; formulas and values are unchanged. */}
                  <tr>
                    <th className="p-3 text-left font-semibold">Month</th>
                    <th className="p-3 text-left font-semibold">Buyer</th>
                    <th className="p-3 text-left font-semibold">SKU</th>
                    <th className="p-3 text-right font-semibold">Forecast Qty</th>
                    <th className="p-3 text-right font-semibold">
                      <TooltipProvider delayDuration={200}>
                        <span className="inline-flex items-center gap-1 justify-end">
                          Completed
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" aria-label="About Completed" data-testid="ps-tooltip-completed" className="text-zinc-400 hover:text-zinc-600 focus:outline-none">
                                <HelpCircle className="w-3.5 h-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-xs leading-snug">
                              Total units completed across all production schedules for this forecast — includes both on-hand and dispatched inventory for this month.
                            </TooltipContent>
                          </Tooltip>
                        </span>
                      </TooltipProvider>
                    </th>
                    {/* R16 (Feb 2026) — On-hand Inventory display-only column.
                        Positioned immediately after Completed, per spec.
                        NOT summed into Total Coverage / Gap. */}
                    <th className="p-3 text-right font-semibold">
                      <TooltipProvider delayDuration={200}>
                        <span className="inline-flex items-center gap-1 justify-end">
                          On-hand Inventory
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" aria-label="About On-hand Inventory" data-testid="ps-tooltip-on-hand" className="text-zinc-400 hover:text-zinc-600 focus:outline-none">
                                <HelpCircle className="w-3.5 h-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-xs leading-snug">
                              On-hand inventory = current stock for this SKU, summed across branches. Equivalent to Completed − Dispatched — already counted inside Completed, not added again to Total coverage or Gap. May include stock not yet tied to a specific forecast.
                            </TooltipContent>
                          </Tooltip>
                        </span>
                      </TooltipProvider>
                    </th>
                    <th className="p-3 text-right font-semibold">
                      <TooltipProvider delayDuration={200}>
                        <span className="inline-flex items-center gap-1 justify-end">
                          Remaining
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" aria-label="About Remaining" data-testid="ps-tooltip-remaining" className="text-zinc-400 hover:text-zinc-600 focus:outline-none">
                                <HelpCircle className="w-3.5 h-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-xs leading-snug">
                              Target quantity on the current active production schedule for this forecast. 0 if nothing is currently scheduled.
                            </TooltipContent>
                          </Tooltip>
                        </span>
                      </TooltipProvider>
                    </th>
                    <th className="p-3 text-right font-semibold">
                      <TooltipProvider delayDuration={200}>
                        <span className="inline-flex items-center gap-1 justify-end">
                          Total Coverage
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" aria-label="About Total Coverage" data-testid="ps-tooltip-total-coverage" className="text-zinc-400 hover:text-zinc-600 focus:outline-none">
                                <HelpCircle className="w-3.5 h-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-xs leading-snug">
                              Completed + Remaining — everything already produced or currently scheduled against this forecast.
                            </TooltipContent>
                          </Tooltip>
                        </span>
                      </TooltipProvider>
                    </th>
                    <th className="p-3 text-right font-semibold">
                      <TooltipProvider delayDuration={200}>
                        <span className="inline-flex items-center gap-1 justify-end">
                          Gap
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" aria-label="About Gap" data-testid="ps-tooltip-gap" className="text-zinc-400 hover:text-zinc-600 focus:outline-none">
                                <HelpCircle className="w-3.5 h-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-xs leading-snug">
                              Total coverage − Forecast qty. Green = produced/scheduled at or above forecast. Red = short of forecast.
                            </TooltipContent>
                          </Tooltip>
                        </span>
                      </TooltipProvider>
                    </th>
                    <th className="p-3 text-left font-semibold">Latest Target Date</th>
                    <th className="p-3 text-center font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPsRows.map((r) => {
                    // Gap colouring — R9 (Feb 2026) — flipped:
                    //   gap = total_coverage − forecast_qty
                    //   gap > 0 → green (surplus / over-produced)
                    //   gap < 0 → red (shortfall)
                    //   gap = 0 → neutral
                    const gap = r.gap || 0;
                    const gapClass = gap > 0 ? "text-green-700 font-semibold"
                      : gap < 0 ? "text-red-700 font-semibold"
                      : "text-zinc-700";
                    return (
                      <tr key={r.forecast_id} className="border-b hover:bg-zinc-50" data-testid={`ps-row-${r.forecast_id}`}>
                        <td className="p-3 font-mono">{r.forecast_month}</td>
                        <td className="p-3">{r.buyer_name || "—"}</td>
                        <td className="p-3 font-mono">{r.buyer_sku_id || "—"}</td>
                        <td className="p-3 text-right font-bold">{Number(r.forecast_qty || 0).toLocaleString()}</td>
                        <td className="p-3 text-right">{Number(r.completed || 0).toLocaleString()}</td>
                        {/* R16 (Feb 2026) — display-only on-hand column. */}
                        <td className="p-3 text-right font-mono" data-testid={`ps-onhand-${r.forecast_id}`}>
                          {Number(r.on_hand_inventory || 0).toLocaleString()}
                        </td>
                        <td className="p-3 text-right">{Number(r.remaining_committed || 0).toLocaleString()}</td>
                        <td className="p-3 text-right">{Number(r.total_coverage || 0).toLocaleString()}</td>
                        <td className={`p-3 text-right ${gapClass}`} data-testid={`ps-gap-${r.forecast_id}`}>
                          {gap > 0 ? "+" : ""}{Number(gap).toLocaleString()}
                        </td>
                        <td className="p-3">{r.latest_target_date || <span className="text-zinc-400">—</span>}</td>
                        {/* R17+R20 (Jul 2026) — dual-badge status cell.
                            No more single-string collapse — the row's
                            own triple (override_status, time_status,
                            qty_status) drives the badges directly. */}
                        <td className="p-3 text-center">
                          <StatusBadges
                            overrideStatus={r.override_status}
                            timeStatus={r.time_status}
                            qtyStatus={r.qty_status}
                            testIdPrefix={`ps-status-${r.forecast_id}`}
                            className="justify-center"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="forecast-summary" data-testid="forecast-summary-content">
          <ForecastSummary />
        </TabsContent>
      </Tabs>

      {/* Bulk Upload Preview Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Bulk Upload Preview
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Feb 2026 (BULK-UPLOAD-OVERFLOW) — single-line aggregate
                summary. Replaces the previous 2-card layout that took
                ~90px + the inline error/impact lists below that were
                pushing Submit out of view on real-sized batches.
                Errors detail: existing Download Error Report button.
                Impact detail: auto-downloaded CSV on preview change
                (see downloadImpactReport helper + effect above). */}
            <div
              className="flex items-center justify-between rounded-lg border bg-zinc-50 px-4 py-2 text-sm"
              data-testid="bulk-upload-summary-line"
            >
              <span className="font-mono text-zinc-700">
                <span className="text-zinc-900 font-semibold">{uploadPreview.length + uploadErrors.length}</span>
                {" rows · "}
                <span className="text-green-700 font-semibold">{uploadPreview.length}</span>
                {" valid · "}
                <span className={uploadErrors.length > 0 ? "text-red-700 font-semibold" : "text-zinc-500"}>{uploadErrors.length}</span>
                {" errors"}
              </span>
            </div>

            {/* Errors summary — download button carries the full detail.
                Inline per-row table removed (BULK-UPLOAD-OVERFLOW). */}
            {uploadErrors.length > 0 && (
              <div className="border border-red-200 rounded-lg overflow-hidden">
                <div className="bg-red-50 px-4 py-2 flex items-center justify-between">
                  <span className="text-sm font-bold text-red-700">
                    <AlertTriangle className="w-4 h-4 inline mr-2" />
                    {uploadErrors.length} rows have errors — download the report for row-level detail
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadErrorReport}
                    disabled={downloadingErrors}
                    className="text-red-700 border-red-300 hover:bg-red-100"
                    data-testid="bulk-upload-download-errors-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {downloadingErrors ? 'Downloading...' : 'Download Error Report'}
                  </Button>
                </div>
              </div>
            )}

            {/* Feb 2026 (BULK-UPLOAD-OVERFLOW) — aggregate impact
                summary. Full per-buyer detail auto-downloads as a CSV
                the moment /bulk-upload/preview resolves (see
                downloadImpactReport + effect). Per-buyer inline card
                stack removed. */}
            {previewImpact && previewImpact.plans && previewImpact.plans.length > 0 && (
              <div className="border border-amber-300 rounded-lg overflow-hidden bg-amber-50" data-testid="bulk-upload-impact-preview">
                <div className="bg-amber-100 px-4 py-2 flex items-start gap-2 text-sm font-bold text-amber-800">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Impact —{" "}
                    <span data-testid="bulk-upload-impact-versioned-count">{impactSummary.versionedPairs}</span>
                    {" buyer/month set"}{impactSummary.versionedPairs === 1 ? "" : "s"}
                    {" versioned · "}
                    <span data-testid="bulk-upload-impact-safety-net-count">{impactSummary.safetyNetCount}</span>
                    {" SKU"}{impactSummary.safetyNetCount === 1 ? "" : "s"}
                    {" safety-net zeroed · "}
                    <span data-testid="bulk-upload-impact-dispatch-count">{impactSummary.dispatchLotsCount}</span>
                    {" dispatch lot"}{impactSummary.dispatchLotsCount === 1 ? "" : "s"}
                    {" re-linked · "}
                    <span data-testid="bulk-upload-impact-schedules-count">{impactSummary.schedulesCount}</span>
                    {" schedule"}{impactSummary.schedulesCount === 1 ? "" : "s"}
                    {" re-linked. "}
                    <span className="font-normal text-amber-700">Full per-buyer detail downloaded automatically.</span>
                  </span>
                </div>
              </div>
            )}
            
            {/* Valid Forecasts Table */}
            {uploadPreview.length > 0 && (
              <div className="border rounded-sm max-h-60 overflow-y-auto">
                {/* Warning if rows missing buyer */}
                {uploadPreview.some(row => !row.buyer_id) && (
                  <div className="bg-red-100 border-b border-red-200 px-4 py-2 text-sm font-bold text-red-700 sticky top-0 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {uploadPreview.filter(row => !row.buyer_id).length} rows are missing Buyer - these will fail on upload!
                  </div>
                )}
                <div className={`px-4 py-2 text-sm font-bold sticky ${uploadPreview.some(row => !row.buyer_id) ? 'top-10' : 'top-0'} ${uploadPreview.every(row => row.buyer_id) ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                  {uploadPreview.filter(row => row.buyer_id).length} Valid / {uploadPreview.filter(row => !row.buyer_id).length} Missing Buyer
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 sticky top-8">
                    <tr>
                      <th className="p-2 text-left font-mono text-xs">Month</th>
                      <th className="p-2 text-left font-mono text-xs">Vertical</th>
                      <th className="p-2 text-left font-mono text-xs">Model</th>
                      <th className="p-2 text-left font-mono text-xs">Brand</th>
                      <th className="p-2 text-left font-mono text-xs">SKU</th>
                      <th className="p-2 text-left font-mono text-xs">Buyer</th>
                      <th className="p-2 text-left font-mono text-xs">Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadPreview.map((row, idx) => (
                      <tr key={idx} className={`border-t hover:bg-zinc-50 ${!row.buyer_id ? 'bg-red-50' : ''}`}>
                        <td className="p-2 font-mono">{row.month}</td>
                        <td className="p-2 text-zinc-600">{row.vertical || '-'}</td>
                        <td className="p-2 text-zinc-600">{row.model || '-'}</td>
                        <td className="p-2 text-zinc-600">{row.brand || '-'}</td>
                        <td className="p-2 font-mono font-bold">{row.sku_id}</td>
                        <td className={`p-2 ${row.buyer_id ? 'text-zinc-600' : 'text-red-600 font-bold'}`}>
                          {row.buyer || <span className="text-red-500">⚠ Missing</span>}
                        </td>
                        <td className="p-2 font-mono font-bold text-green-600">{row.quantity?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {uploadPreview.length === 0 && uploadErrors.length === 0 && (
              <div className="text-center py-8 text-zinc-500">
                No data found in the uploaded file
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              <strong>Required columns:</strong> Month, SKU ID, Quantity, Buyer<br/>
              <strong>Optional columns:</strong> Vertical, Brand, Model (auto-filled from SKU master)
            </p>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setShowUploadDialog(false); setUploadErrors([]); setPreviewImpact(null); setPendingUploadFile(null); }} className="flex-1" data-testid="bulk-upload-cancel-btn">
                Cancel
              </Button>
              <Button 
                onClick={handleBulkUpload} 
                disabled={uploading || uploadPreview.length === 0 || !pendingUploadFile} 
                className="flex-1"
                data-testid="bulk-upload-confirm-btn"
              >
                {uploading ? 'Uploading...' : (
                  previewImpact && previewImpact.plans && previewImpact.plans.length > 0
                    ? `Confirm & Version ${previewImpact.total_pairs} Buyer/Month${previewImpact.total_pairs === 1 ? '' : 's'}`
                    : `Upload ${uploadPreview.length} Forecasts`
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* R3 + R4 (Feb 2026): Forecast Details — version history + linked production schedules.
          Triggered by the FileText icon on every forecast row. View-only. */}
      <Dialog open={!!detailsForecast} onOpenChange={(open) => { if (!open) { setDetailsForecast(null); setDetailsSchedules([]); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="forecast-details-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Forecast Details — {detailsForecast?.forecast_code}
            </DialogTitle>
          </DialogHeader>
          {detailsForecast && (
            <div className="space-y-6">
              {/* Header summary */}
              <div className="grid grid-cols-2 gap-3 p-3 bg-zinc-50 rounded-lg border text-sm">
                <div><span className="text-zinc-500">Buyer:</span> <span className="font-medium">{getBuyerName(detailsForecast.buyer_id)}</span></div>
                <div><span className="text-zinc-500">SKU:</span> <span className="font-mono">{detailsForecast.sku_id || 'All in Vertical'}</span></div>
                <div><span className="text-zinc-500">Current qty:</span> <span className="font-mono font-bold">{(detailsForecast.quantity || 0).toLocaleString()}</span></div>
              </div>

              {/* ─────── R4: Forecast Version History ─────── */}
              <div>
                <h3 className="font-bold uppercase text-sm tracking-wide mb-2 flex items-center gap-2">
                  <History className="w-4 h-4" /> Forecast Version History
                </h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm" data-testid="forecast-version-history-table">
                    <thead className="bg-zinc-100">
                      <tr>
                        <th className="h-9 px-3 text-left font-mono text-xs uppercase">#</th>
                        <th className="h-9 px-3 text-left font-mono text-xs uppercase">Version</th>
                        <th className="h-9 px-3 text-left font-mono text-xs uppercase">Edited at</th>
                        <th className="h-9 px-3 text-right font-mono text-xs uppercase">Quantity</th>
                        <th className="h-9 px-3 text-left font-mono text-xs uppercase">Date</th>
                        <th className="h-9 px-3 text-left font-mono text-xs uppercase">By</th>
                        <th className="h-9 px-3 text-left font-mono text-xs uppercase">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Row 1 = Original. Lazy backfill: if the forecast has never been
                        // edited (no version_history yet), we still show the current values
                        // as Original — they ARE the original from the forecast's POV.
                        const history = detailsForecast.version_history || [];
                        const originalQty = detailsForecast.original_quantity ?? detailsForecast.quantity;
                        const originalDate = detailsForecast.original_date ?? detailsForecast.forecast_month;
                        const fmtDate = (d) => {
                          if (!d) return "—";
                          const s = typeof d === "string" ? d : d.toString();
                          return s.length >= 10 ? s.slice(0, 10) : s;
                        };
                        const rows = [];
                        rows.push(
                          <tr key="v1" className="border-t bg-blue-50/30">
                            <td className="p-3 font-mono">1</td>
                            <td className="p-3"><span className="text-xs font-mono px-2 py-0.5 rounded border bg-blue-100 border-blue-300 text-blue-700">Original</span></td>
                            <td className="p-3 text-zinc-600">{fmtDate(detailsForecast.created_at)}</td>
                            <td className="p-3 text-right font-mono font-bold">{(originalQty || 0).toLocaleString()}</td>
                            <td className="p-3 font-mono">{fmtDate(originalDate)}</td>
                            <td className="p-3 text-zinc-600">{detailsForecast.created_by_name || detailsForecast.created_by || "—"}</td>
                            <td className="p-3 text-zinc-400">—</td>
                          </tr>
                        );
                        history.forEach((h, idx) => {
                          const isLast = idx === history.length - 1;
                          rows.push(
                            <tr key={`v${h.version || idx + 2}`} className={`border-t ${isLast ? 'bg-green-50/30' : ''}`}>
                              <td className="p-3 font-mono">{idx + 2}</td>
                              <td className="p-3">
                                {isLast ? (
                                  <span className="text-xs font-mono px-2 py-0.5 rounded border bg-green-100 border-green-300 text-green-700">Latest</span>
                                ) : (
                                  <span className="text-xs font-mono">Edit</span>
                                )}
                              </td>
                              <td className="p-3 text-zinc-600">{fmtDate(h.edited_at)}</td>
                              <td className="p-3 text-right font-mono">
                                <span className="text-zinc-400 line-through mr-2">{(h.old_quantity ?? 0).toLocaleString()}</span>
                                <span className="font-bold">{(h.new_quantity ?? 0).toLocaleString()}</span>
                              </td>
                              <td className="p-3 font-mono">
                                {h.old_date !== h.new_date ? (
                                  <>
                                    <span className="text-zinc-400 line-through mr-1">{fmtDate(h.old_date)}</span>
                                    <span>{fmtDate(h.new_date)}</span>
                                  </>
                                ) : (
                                  <span>{fmtDate(h.new_date)}</span>
                                )}
                              </td>
                              <td className="p-3 text-zinc-600">{h.edited_by_name || h.edited_by || "—"}</td>
                              <td className="p-3 text-zinc-700 italic">{h.reason || <span className="text-zinc-400">—</span>}</td>
                            </tr>
                          );
                        });
                        return rows;
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ─────── R11 (Feb 2026): 3-block Schedule History ─────── */}
              <div>
                <h3 className="font-bold uppercase text-sm tracking-wide mb-2 flex items-center gap-2">
                  <Package className="w-4 h-4" /> Production Schedule History
                </h3>
                {detailsLoading ? (
                  <div className="p-6 text-center text-zinc-500 border rounded-lg">Loading…</div>
                ) : detailsSchedules.length === 0 ? (
                  <div className="p-6 text-center text-zinc-500 border rounded-lg">No production schedules linked to this forecast yet.</div>
                ) : (
                  (() => {
                    const fmtDate = (d) => {
                      if (!d) return "—";
                      const s = typeof d === "string" ? d : d.toString();
                      return s.length >= 10 ? s.slice(0, 10) : s;
                    };
                    const parseDate = (d) => {
                      if (!d) return null;
                      const s = typeof d === "string" ? d : d.toString();
                      const dt = new Date(s);
                      return isNaN(dt.getTime()) ? null : dt;
                    };
                    const today = new Date(); today.setHours(0, 0, 0, 0);
                    const forecastQty = detailsForecast.quantity || 0;

                    // Block 1 — Original Plan: is_original_plan === true (any status incl DELETED).
                    const originalRows = detailsSchedules
                      .filter(s => s.is_original_plan === true)
                      .sort((a, b) => (parseDate(a.target_date)?.getTime() || 0) - (parseDate(b.target_date)?.getTime() || 0));
                    // Block 2 — Latest Plan: exclude DELETED, CANCELLED,
                    // and the original-plan schedule(s) (they already
                    // appear in Block 1 — Rahul, Jul 2026). COMPLETED
                    // rows are INCLUDED: coverage math is fully
                    // server-side and doesn't read this array, so the
                    // prior Feb 2026 Q2a COMPLETED-exclusion (added to
                    // avoid double-counting `completed_quantity`) is
                    // vestigial and was silently hiding every completed
                    // schedule from operators.
                    // Extracted to a helper so it's unit-tested — see
                    // /app/frontend/src/__tests__/latestPlanRows.test.js.
                    const latestRows = filterLatestPlanRows(detailsSchedules)
                      .sort((a, b) => (parseDate(a.target_date)?.getTime() || 0) - (parseDate(b.target_date)?.getTime() || 0));

                    // R17+R20 (Jul 2026) — Block 1 / Block 2 badges
                    // read the triple fields directly. The legacy
                    // single-string flatten fields were retired in the
                    // Jul 2026 shim cleanup.
                    const origTriple = {
                      overrideStatus: detailsPlanStatuses?.original_plan_override_status || null,
                      timeStatus:     detailsPlanStatuses?.original_plan_time_status || null,
                      qtyStatus:      detailsPlanStatuses?.original_plan_qty_status || null,
                    };
                    const latestTriple = {
                      overrideStatus: detailsPlanStatuses?.latest_plan_override_status || null,
                      timeStatus:     detailsPlanStatuses?.latest_plan_time_status || null,
                      qtyStatus:      detailsPlanStatuses?.latest_plan_qty_status || null,
                    };
                    // NO_SCHEDULE fallback — when the endpoint hasn't
                    // returned any status at all (e.g. still loading),
                    // show a NO_SCHEDULE placeholder so the block
                    // header isn't blank.
                    if (!origTriple.overrideStatus && !origTriple.timeStatus) {
                      origTriple.overrideStatus = "NO_SCHEDULE";
                    }
                    if (!latestTriple.overrideStatus && !latestTriple.timeStatus) {
                      latestTriple.overrideStatus = "NO_SCHEDULE";
                    }

                    // Block 3 — revision count.
                    const revisionBatchIds = new Set(
                      detailsSchedules
                        .filter(s => s.is_original_plan === false && s.plan_batch_id)
                        .map(s => s.plan_batch_id)
                    );
                    const revisionCount = revisionBatchIds.size;

                    const renderRow = (s, idx) => {
                      const isDel = s.status === "DELETED";
                      return (
                        <tr key={s.id || s.schedule_code} className={`border-t ${isDel ? 'text-zinc-400 line-through bg-zinc-50' : ''}`}>
                          <td className="p-3 font-mono text-xs">{s.schedule_code}</td>
                          <td className="p-3 text-right font-mono">{(s.target_quantity || 0).toLocaleString()}</td>
                          <td className="p-3 font-mono">{fmtDate(s.target_date)}</td>
                          {/* Feb 2026 — actual-completion date paired next to
                              Target Date so planners can eyeball planned-vs-actual
                              at a glance. `completed_at` flows through untouched
                              from cpc_routes.get_production_schedules (find with
                              `_id: 0` projection). fmtDate returns "—" for
                              null/undefined so schedules that never completed
                              render blank cleanly (AC3). */}
                          <td className="p-3 font-mono">{fmtDate(s.completed_at)}</td>
                          <td className="p-3 text-right font-mono">{(s.completed_quantity || 0).toLocaleString()}</td>
                          <td className="p-3"><span className={`text-xs font-mono px-2 py-0.5 rounded border ${getStatusColor(s.status)}`}>{s.status}</span></td>
                        </tr>
                      );
                    };

                    return (
                      <div className="space-y-4">
                        {/* Block 1 — Original Plan */}
                        <div className="border rounded-lg overflow-hidden" data-testid="forecast-block-original-plan">
                          <div className="flex items-center justify-between bg-blue-50 px-4 py-2 border-b">
                            <div className="font-semibold text-sm">Block 1 — Original Plan</div>
                            <StatusBadges
                              overrideStatus={origTriple.overrideStatus}
                              timeStatus={origTriple.timeStatus}
                              qtyStatus={origTriple.qtyStatus}
                              testIdPrefix="original-plan-status"
                            />
                          </div>
                          {originalRows.length === 0 ? (
                            <div className="p-4 text-sm text-zinc-500">No original-plan schedules recorded.</div>
                          ) : (
                            <table className="w-full text-sm">
                              <thead className="bg-zinc-100">
                                <tr>
                                  <th className="h-9 px-3 text-left font-mono text-xs uppercase">Schedule ID</th>
                                  <th className="h-9 px-3 text-right font-mono text-xs uppercase">Target Qty</th>
                                  <th className="h-9 px-3 text-left font-mono text-xs uppercase">Target Date</th>
                                  <th className="h-9 px-3 text-left font-mono text-xs uppercase">Completed Date</th>
                                  <th className="h-9 px-3 text-right font-mono text-xs uppercase">Completed</th>
                                  <th className="h-9 px-3 text-left font-mono text-xs uppercase">Status</th>
                                </tr>
                              </thead>
                              <tbody>{originalRows.map(renderRow)}</tbody>
                            </table>
                          )}
                        </div>

                        {/* Block 2 — Latest Plan */}
                        <div className="border rounded-lg overflow-hidden" data-testid="forecast-block-latest-plan">
                          <div className="flex items-center justify-between bg-green-50 px-4 py-2 border-b">
                            <div className="font-semibold text-sm">Block 2 — Latest Plan</div>
                            <StatusBadges
                              overrideStatus={latestTriple.overrideStatus}
                              timeStatus={latestTriple.timeStatus}
                              qtyStatus={latestTriple.qtyStatus}
                              testIdPrefix="latest-plan-status"
                            />
                          </div>
                          {latestRows.length === 0 ? (
                            <div className="p-4 text-sm text-zinc-500">No active schedules.</div>
                          ) : (
                            <table className="w-full text-sm">
                              <thead className="bg-zinc-100">
                                <tr>
                                  <th className="h-9 px-3 text-left font-mono text-xs uppercase">Schedule ID</th>
                                  <th className="h-9 px-3 text-right font-mono text-xs uppercase">Target Qty</th>
                                  <th className="h-9 px-3 text-left font-mono text-xs uppercase">Target Date</th>
                                  <th className="h-9 px-3 text-left font-mono text-xs uppercase">Completed Date</th>
                                  <th className="h-9 px-3 text-right font-mono text-xs uppercase">Completed</th>
                                  <th className="h-9 px-3 text-left font-mono text-xs uppercase">Status</th>
                                </tr>
                              </thead>
                              <tbody>{latestRows.map(renderRow)}</tbody>
                            </table>
                          )}
                        </div>

                        {/* Block 3 — Iterations */}
                        <div className="text-sm text-zinc-600 italic px-1" data-testid="forecast-block-iterations">
                          {revisionCount === 0
                            ? "No revisions to original plan"
                            : `Plan revised ${revisionCount} time${revisionCount === 1 ? "" : "s"}`}
                        </div>
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dispatch Lots Popup Dialog */}
      <Dialog open={showLotsDialog} onOpenChange={setShowLotsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Dispatch Lots - {selectedForecastLots.forecast?.forecast_code}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedForecastLots.forecast && (
              <div className="p-3 bg-zinc-50 rounded-lg text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-zinc-500">SKU:</span> <span className="font-mono font-bold">{selectedForecastLots.forecast.sku_id}</span></div>
                  <div><span className="text-zinc-500">Forecast Qty:</span> <span className="font-mono font-bold">{selectedForecastLots.forecast.quantity?.toLocaleString()}</span></div>
                  <div><span className="text-zinc-500">Buyer:</span> {getBuyerName(selectedForecastLots.forecast.buyer_id)}</div>
                  <div><span className="text-zinc-500">Month:</span> {selectedForecastLots.forecast.forecast_month?.slice(0, 7)}</div>
                  <div><span className="text-zinc-500">Dispatch Allocated:</span> <span className="font-mono text-blue-600">{(selectedForecastLots.forecast.dispatch_allocated || 0).toLocaleString()}</span></div>
                  <div>
                    <span className="text-zinc-500">Available to Allocate:</span> 
                    <span className="font-mono font-bold text-green-600 ml-1">
                      {Math.max(0, (selectedForecastLots.forecast.quantity || 0) - (selectedForecastLots.forecast.dispatch_allocated || 0)).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Linked Dispatch Lots */}
            {selectedForecastLots.lots.length > 0 ? (
              <div className="space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-600">{selectedForecastLots.lots.length}</div>
                    <div className="text-xs text-blue-700 uppercase">Dispatch Lots</div>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {selectedForecastLots.lots.reduce((sum, l) => sum + (l.forecast_qty_in_lot || l.total_quantity || 0), 0).toLocaleString()}
                    </div>
                    <div className="text-xs text-green-700 uppercase">Total Qty Assigned</div>
                  </div>
                  <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-zinc-600">
                      {selectedForecastLots.forecast?.quantity?.toLocaleString() || 0}
                    </div>
                    <div className="text-xs text-zinc-500 uppercase">Forecast Qty</div>
                  </div>
                </div>

                {/* Lots Table */}
                <div className="border rounded overflow-hidden">
                  <div className="bg-zinc-100 px-4 py-2 text-xs font-bold uppercase">Linked Dispatch Lots</div>
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-mono text-xs uppercase">Lot Code</th>
                        <th className="px-4 py-2 text-left font-mono text-xs uppercase">Buyer</th>
                        <th className="px-4 py-2 text-right font-mono text-xs uppercase">Qty in Lot</th>
                        <th className="px-4 py-2 text-center font-mono text-xs uppercase">Lines</th>
                        <th className="px-4 py-2 text-center font-mono text-xs uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedForecastLots.lots.map((lot, i) => (
                        <tr key={i} className="border-t hover:bg-zinc-50">
                          <td className="px-4 py-3 font-mono font-bold text-primary">{lot.lot_code}</td>
                          <td className="px-4 py-3 text-sm text-zinc-600">{lot.buyer_name || '-'}</td>
                          <td className="px-4 py-3 font-mono font-bold text-right text-green-600">
                            {(lot.forecast_qty_in_lot || lot.total_quantity || lot.required_quantity || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="bg-zinc-100 text-zinc-700 text-xs px-2 py-1 rounded font-mono">
                              {lot.forecast_lines?.length || lot.line_count || 1}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-xs font-mono px-2 py-1 rounded border ${
                              lot.status === 'DISPATCHED' || lot.status === 'DELIVERED' ? 'bg-green-100 text-green-700 border-green-300' :
                              lot.status === 'READY' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                              lot.status === 'CREATED' ? 'bg-zinc-100 border-zinc-300' :
                              'bg-yellow-100 text-yellow-700 border-yellow-300'
                            }`}>{lot.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-zinc-50 border-t">
                      <tr>
                        <td colSpan={2} className="px-4 py-2 font-bold text-right">Total:</td>
                        <td className="px-4 py-2 font-mono font-bold text-right text-green-600">
                          {selectedForecastLots.lots.reduce((sum, l) => sum + (l.forecast_qty_in_lot || l.total_quantity || l.required_quantity || 0), 0).toLocaleString()}
                        </td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Line Details */}
                {selectedForecastLots.lots.some(l => l.forecast_lines?.length > 0) && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-zinc-100 px-4 py-2 text-xs font-bold uppercase">Line Details</div>
                    <div className="p-2 space-y-1 max-h-48 overflow-y-auto">
                      {selectedForecastLots.lots.map((lot) => (
                        lot.forecast_lines?.map((line, idx) => (
                          <div key={`${lot.id}-${idx}`} className="flex items-center justify-between bg-zinc-50 rounded px-3 py-2 text-sm">
                            <span className="font-mono text-zinc-600">{lot.lot_code} / Line {line.line_number}</span>
                            <span className="font-mono">{line.sku_id}</span>
                            <span className="font-mono font-bold">{line.quantity?.toLocaleString()}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              line.status === 'READY' ? 'bg-green-100 text-green-700' :
                              line.status === 'PENDING' ? 'bg-zinc-100 text-zinc-600' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>{line.status || 'PENDING'}</span>
                          </div>
                        ))
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 text-center text-zinc-500 border rounded-lg bg-zinc-50">
                <Package className="w-6 h-6 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No dispatch lots linked to this forecast yet.</p>
              </div>
            )}
            
            {/* Allocate to Dispatch Lot — R13 (Jul 2026) follow-up:
                previously gated on `status !== 'DRAFT'`, which post-R13
                silently locked every DRAFT forecast (i.e. nearly all
                of them) out of allocation. Backend endpoints do not
                gate on forecast.status — the section renders on
                unallocated-capacity alone, via the extracted pure
                predicate (see lib/dispatchAllocateVisibility.js). */}
            {shouldShowAllocateSection(selectedForecastLots.forecast) && (
              <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
                <div className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Allocate to Dispatch Lot
                </div>
                
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Quantity to Allocate</Label>
                    <Input
                      type="number"
                      value={addToLotQty}
                      onChange={(e) => setAddToLotQty(parseInt(e.target.value) || 0)}
                      max={selectedForecastLots.forecast.quantity - (selectedForecastLots.forecast.dispatch_allocated || 0)}
                      className="font-mono"
                      placeholder="Enter quantity"
                    />
                    <p className="text-xs text-blue-600 mt-1">
                      Max: {(selectedForecastLots.forecast.quantity - (selectedForecastLots.forecast.dispatch_allocated || 0)).toLocaleString()}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    {/* Add to Existing Lot */}
                    <div className="space-y-2">
                      <Label className="text-xs">Add to Existing Lot (Same Buyer)</Label>
                      <Select value={selectedLotForAdd || undefined} onValueChange={setSelectedLotForAdd}>
                        <SelectTrigger className="text-sm">
                          <SelectValue placeholder="Select lot..." />
                        </SelectTrigger>
                        <SelectContent>
                          {buyerExistingLots.length === 0 ? (
                            <SelectItem value="_none" disabled>No existing lots for this buyer</SelectItem>
                          ) : (
                            buyerExistingLots.filter(lot => lot.id).map(lot => (
                              <SelectItem key={lot.id} value={lot.id}>
                                {lot.lot_number || lot.lot_code} ({(lot.total_quantity || 0).toLocaleString()} units)
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full text-xs"
                        disabled={!selectedLotForAdd || addToLotQty <= 0 || addingToLot}
                        onClick={async () => {
                          if (!selectedLotForAdd || addToLotQty <= 0) return;
                          setAddingToLot(true);
                          try {
                            // V2 add-line (Feb 2026 fix). V2 writes the line
                            // into the embedded `lines[]` array on the lot,
                            // which is what the Dispatch Lots page reads.
                            await axios.post(
                              `${API}/dispatch-lots-v2/${selectedLotForAdd}/add-line`,
                              {
                                buyer_sku_id: selectedForecastLots.forecast.sku_id,
                                quantity: addToLotQty,
                                forecast_id: selectedForecastLots.forecast.id,
                              },
                              { headers: getHeaders() }
                            );
                            toast.success(`Added ${addToLotQty} units to dispatch lot`);
                            setShowLotsDialog(false);
                            setAddToLotQty(0);
                            setSelectedLotForAdd("");
                            fetchAllData();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || "Failed to add to lot");
                          } finally {
                            setAddingToLot(false);
                          }
                        }}
                      >
                        {addingToLot ? "Adding..." : "Add to Lot"}
                      </Button>
                    </div>
                    
                    {/* Create New Lot */}
                    <div className="space-y-2">
                      <Label className="text-xs">Or Create New Dispatch Lot</Label>
                      <p className="text-xs text-zinc-500">
                        Create a new lot for {getBuyerName(selectedForecastLots.forecast.buyer_id)}
                      </p>
                      <Button
                        size="sm"
                        className="w-full text-xs"
                        disabled={addToLotQty <= 0 || addingToLot}
                        onClick={async () => {
                          if (addToLotQty <= 0) {
                            toast.error("Enter a quantity first");
                            return;
                          }
                          setAddingToLot(true);
                          try {
                            // V2 create (Feb 2026 fix). Two-step:
                            //   1) create the lot with the line included so
                            //      V2 stores customer_name + embedded lines[]
                            //      that the Dispatch Lots page reads.
                            //   2) call add-line to attach `forecast_id` and
                            //      bump master_demand_forecast.dispatch_allocated_quantity
                            //      (V2 create doesn't persist the forecast back-link).
                            // We pass buyer.customer_code when available so the
                            // V2 list filter by customer_code matches; UUID is
                            // also accepted by the V2 buyer lookup.
                            const buyer = buyers.find((b) => b.id === selectedForecastLots.forecast.buyer_id);
                            const customerId = buyer?.customer_code || selectedForecastLots.forecast.buyer_id;
                            const createRes = await axios.post(
                              `${API}/dispatch-lots-v2`,
                              {
                                customer_id: customerId,
                                lines: [],   // line added in step 2 so forecast_id survives
                                notes: `Created from forecast ${selectedForecastLots.forecast.forecast_code}`,
                              },
                              { headers: getHeaders() }
                            );
                            const newLotId = createRes.data?.id;
                            if (newLotId) {
                              await axios.post(
                                `${API}/dispatch-lots-v2/${newLotId}/add-line`,
                                {
                                  buyer_sku_id: selectedForecastLots.forecast.sku_id,
                                  quantity: addToLotQty,
                                  forecast_id: selectedForecastLots.forecast.id,
                                },
                                { headers: getHeaders() }
                              );
                            }
                            toast.success(`Created new dispatch lot with ${addToLotQty} units`);
                            setShowLotsDialog(false);
                            setAddToLotQty(0);
                            fetchAllData();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || "Failed to create lot");
                          } finally {
                            setAddingToLot(false);
                          }
                        }}
                      >
                        {addingToLot ? "Creating..." : "Create New Lot"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <Button variant="outline" onClick={() => setShowLotsDialog(false)} className="w-full">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Forecasts Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Export Forecasts
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-500">
              Export forecast data to Excel for creating dispatch lot bulk uploads.
            </p>
            
            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Start Month</Label>
                <Input 
                  type="month" 
                  value={exportFilters.startDate}
                  onChange={(e) => setExportFilters({...exportFilters, startDate: e.target.value})}
                  className="text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">End Month</Label>
                <Input 
                  type="month" 
                  value={exportFilters.endDate}
                  onChange={(e) => setExportFilters({...exportFilters, endDate: e.target.value})}
                  className="text-sm"
                />
              </div>
            </div>
            
            {/* Buyer Filter */}
            <div>
              <Label className="text-xs">Buyer</Label>
              <Select 
                value={exportFilters.buyer_id || "_all"} 
                onValueChange={(v) => setExportFilters({...exportFilters, buyer_id: v})}
              >
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder="All Buyers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All Buyers</SelectItem>
                  {buyers.filter(b => b.id).map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Brand & Model Filters */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Brand</Label>
                <Select 
                  value={exportFilters.brand || "_all"} 
                  onValueChange={(v) => setExportFilters({...exportFilters, brand: v})}
                >
                  <SelectTrigger className="text-sm">
                    <SelectValue placeholder="All Brands" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Brands</SelectItem>
                    {getUniqueBrands().filter(brand => brand).map(brand => (
                      <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Model</Label>
                <Select 
                  value={exportFilters.model || "_all"} 
                  onValueChange={(v) => setExportFilters({...exportFilters, model: v})}
                >
                  <SelectTrigger className="text-sm">
                    <SelectValue placeholder="All Models" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Models</SelectItem>
                    {getUniqueModels().filter(model => model).map(model => (
                      <SelectItem key={model} value={model}>{model}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Status Filter */}
            <div>
              <Label className="text-xs">Status</Label>
              <Select 
                value={exportFilters.status || "_all"} 
                onValueChange={(v) => setExportFilters({...exportFilters, status: v})}
              >
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  {/* R13 (Jul 2026) — CONFIRMED removed from status filter. */}
                  <SelectItem value="_all">All Statuses</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="CONVERTED">Converted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <strong>Tip:</strong> The exported file includes Forecast No, SKU ID, and Available Qty - 
              perfect for creating dispatch lot bulk uploads!
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowExportDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleExportForecasts} 
                disabled={exporting}
                className="flex-1"
              >
                {exporting ? 'Exporting...' : 'Download Excel'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Demand;
